import { OrderedProductFields, isPrintBeforePaper } from "../../components/CompanyAdmin/ProductOption/OrderedProductFields";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuthStore } from "../../stores/auth-store";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import Modal from "../../components/UI/Modal";
import {
  Plus,
  X,
  Camera,
  AlertTriangle,
  Pencil,
  ChevronLeft,
  ChevronRight,
  LucidePlus,
} from "lucide-react";
import type {
  ProductConfigDto,
  CategoryDto,
  ProductPaperDto,
  GroupedProductPaperDto,
  AvailablePaperOptionsDto,
  SizeTemplateDto,
  OptionDto,
  MaterialDto,
  PaperSpecDto,
  MaterialSheetPaperDto,
  MaterialRollPaperDto,
  InkConfigDto,
  ProductInkConfigDto,
  PriceCalculationResult,
  CreateProductRequest,
  UpdateProductRequest,
  CreateProductPaperRequest,
  CreateSizeTemplateRequest,
  CreateOptionRequest,
  CreateOptionAttributeRequest,
  CreateOptionAttributeValueRequest,
  PriceCalculationRequest,
  EstimatePreviewResponse,
  PaperUsageType,
} from "../../../domain/types/company/company-product-system";
import { companyProductSystemApiService } from "../../../infrastructure/api/company/company-product-system-api-service";
import {
  PRODUCT_TYPE_LABELS,
  PAPER_TYPE_LABELS,
  isBookProductType,
  isRollPaperProductType,
} from "../../../domain/types/company/company-product-system";
import type { DetailPageDto } from "../../../domain/types/company/company-product";
import { companyProductSystemUseCase, companyProductUseCase } from "../../../di/container";
import { OptionSelectModal } from "../../components/CompanyAdmin/ProductOption/OptionSelectModal";
import { ImpositionSettingsCard, type ImpositionSettings } from "../../components/CompanyAdmin/ProductManagement/ImpositionSettingsCard";
import { ProductOptionList } from "../../components/CompanyAdmin/ProductOption/ProductOptionList";
import type { PredefinedOptionTemplate } from "../../components/CompanyAdmin/ProductOption/option-constants";
import { DEFAULT_NEW_OPTION_QUANTITY_TYPE } from "../../components/CompanyAdmin/ProductOption/option-constants";
import NotificationsList from "../../components/UI/NotificationList";
import { useNotifications } from "../../hooks/use-notifications";
import { TooltipPortal } from "../../components/UI/TooltipPortal";
import { motion } from "framer-motion";
import { resolveOptionVisibility } from "../../../shared/utils/option-trigger";
import { resolveTierQuantity, resolveValueUnitPrice } from "../../../shared/utils/option-price";

// ===== Tag 색상 팔레트 (피그마 기반) =====
const TAG_COLORS = [
  { bg: "#E8EFFF", text: "#4285F4" },
  { bg: "#EDE7F6", text: "#7B1FA2" },
  { bg: "#E0F2F1", text: "#00897B" },
  { bg: "#FFF3E0", text: "#EF6C00" },
  { bg: "#FCE4EC", text: "#C62828" },
  { bg: "#E8F5E9", text: "#2E7D32" },
];

/** 쇼핑몰 용지 선택지에서 빠지는 용지가 있으면 용지 카드에 사유를 띄운다 */
const ShopUnavailablePaperWarning: React.FC<{ group: GroupedProductPaperDto }> = ({ group }) => {
  const unavailable = group.shopUnavailablePapers ?? [];
  if (unavailable.length === 0) return null;
  return (
    <div className="mt-2 p-2 rounded-[6px] bg-[#EBF3FF] border border-[#4285F4]/20">
      <p className="flex items-center gap-1 text-[11px] font-semibold text-[#4285F4] mb-1">
        <AlertTriangle className="w-[12px] h-[12px] shrink-0" />
        쇼핑몰 미노출
      </p>
      <ul className="space-y-0.5">
        {unavailable.map((p) => (
          <li key={p.paperId} className="text-[11px] leading-[1.4] text-[#4285F4]">
            {p.grammage ? `${p.grammage}g: ` : `${p.materialName}: `}
            {p.message}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ProductSystemDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const companyId = user?.companyId;
  const isEditing = !!productId;

  const { categoryId: initialCategoryId } =
    (location.state as { categoryId?: number }) || {};

  // Notifications
  const { notifications, addItem: addNotificationItem } = useNotifications();

  // Data states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingSizeSettings, setSavingSizeSettings] = useState(false);
  const [categories, setCategories] = useState<CategoryDto[]>([]);
  const [materials, setMaterials] = useState<MaterialDto[]>([]);
  const [paperSpecs, setPaperSpecs] = useState<PaperSpecDto[]>([]);
  const [productConfig, setProductConfig] = useState<ProductConfigDto | null>(null);

  // Product form states
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(initialCategoryId || null);
  const [productType, setProductType] = useState<string>("");
  const [paperType, setPaperType] = useState<string>("");
  const [isProductEnabled, setIsProductEnabled] = useState(true); // 상품 활성화 여부 (판매 중/판매 중지)
  const [applyWasteRate, setApplyWasteRate] = useState(false); // 손지율 적용 여부
  // 낱장 상품의 용지 가격 기준 (기존 / 조판)
  const [impositionSettings, setImpositionSettings] = useState<ImpositionSettings>({
    paperPricingBasis: "SIZE_FIT",
    impositionSheetSizeSpecId: null,
    impositionGapMm: 0,
  });
  const [isQuoteOnly, setIsQuoteOnly] = useState(false); // 견적 문의 전용 상품
  const [isSizeCustomizable, setIsSizeCustomizable] = useState(false);
  const [isInnerPaperCustomizable, setIsInnerPaperCustomizable] = useState(false); // 내지 구간 분할 허용 여부 (책자 상품)
  // 쇼핑몰에서 고를 수 있는 인쇄 방식 (인쇄 구성처럼 허용한 것만 노출)
  const [isSingleSideEnabled, setIsSingleSideEnabled] = useState(true);
  const [isDoubleSideEnabled, setIsDoubleSideEnabled] = useState(true);
  const [minWidth, setMinWidth] = useState<number | null>(null);
  const [maxWidth, setMaxWidth] = useState<number | null>(null);
  const [minHeight, setMinHeight] = useState<number | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  // 원본 값 저장 (변경 감지용)
  const originalValuesRef = useRef<{
    name: string;
    description: string;
  }>({ name: "", description: "" });

  // Thumbnail states
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  // Add this ref at the top of your component with other refs
  const detailPageInputRef = useRef<HTMLInputElement>(null);

  const detailPageChangeCallbackRef = useRef<Function | null>(null);

  const [productDetailImages, setProductDetailImages] = useState<
    DetailPageDto[]
  >([]);

  // Sub-resource states
  const [productPapers, setProductPapers] = useState<ProductPaperDto[]>([]);
  const [groupedPapers, setGroupedPapers] = useState<GroupedProductPaperDto[]>([]);
  const [sizeTemplates, setSizeTemplates] = useState<SizeTemplateDto[]>([]);
  const [options, setOptions] = useState<OptionDto[]>([]);
  const [priceResult, setPriceResult] = useState<PriceCalculationResult | null>(null);
  const [sheetPapersMap, setSheetPapersMap] = useState<Record<number, MaterialSheetPaperDto[]>>({});
  const [rollPapersMap, setRollPapersMap] = useState<Record<number, MaterialRollPaperDto[]>>({});

  // Modal states
  const [showProductTypeModal, setShowProductTypeModal] = useState(false);
  const [showPaperTypeModal, setShowPaperTypeModal] = useState(false);
  const [showAddPaperModal, setShowAddPaperModal] = useState(false);
  const [showDeletePaperModal, setShowDeletePaperModal] = useState(false);
  const [deletePaperGroup, setDeletePaperGroup] = useState<GroupedProductPaperDto | null>(null);
  const [selectedGrammages, setSelectedGrammages] = useState<Set<number>>(new Set());
  const [showAddSizeModal, setShowAddSizeModal] = useState(false);
  const [showOptionSelectModal, setShowOptionSelectModal] = useState(false);
  const [showAddOptionModal, setShowAddOptionModal] = useState(false);
  // 옵션 직접 만들기 상태
  const [customOptionName, setCustomOptionName] = useState("");
  const [customOptionDescription, setCustomOptionDescription] = useState("");
  const [showSizeSettingsModal, setShowSizeSettingsModal] = useState(false);
  const [showDisableCustomSizeModal, setShowDisableCustomSizeModal] = useState(false);
  const [showEditSizeModal, setShowEditSizeModal] = useState(false);
  const [editingSizeId, setEditingSizeId] = useState<number | null>(null);
  const [editSizeWidth, setEditSizeWidth] = useState<number>(0);
  const [editSizeHeight, setEditSizeHeight] = useState<number>(0);
  const [editSizeName, setEditSizeName] = useState<string>("");

  // Size validation errors
  const [sizeValidationErrors, setSizeValidationErrors] = useState<{
    minWidth?: string;
    maxWidth?: string;
    minHeight?: string;
    maxHeight?: string;
  }>({});

  // Temp form states for modals
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(null);
  const [newSizeWidth, setNewSizeWidth] = useState<number>(0);
  const [newSizeHeight, setNewSizeHeight] = useState<number>(0);
  const [newSizeName, setNewSizeName] = useState<string>("");
  const [newOptionName, setNewOptionName] = useState("");
  const [newOptionDescription, setNewOptionDescription] = useState("");

  // 2-step 용지 선택 (상품 용지 추가 시)
  const [paperAddStep, setPaperAddStep] = useState<1 | 2>(1);
  const [selectedPaperBaseName, setSelectedPaperBaseName] = useState<string>("");
  const [selectedPaperUsageType, setSelectedPaperUsageType] = useState<PaperUsageType>("GENERAL");

  // Price calc form - 기본
  const [calcWidth, setCalcWidth] = useState<number>(90);
  const [calcHeight, setCalcHeight] = useState<number>(50);
  const [calcQuantity, setCalcQuantity] = useState<number>(1);

  // Price calc form - 자재/잉크/옵션 선택
  const [calcMaterialId, setCalcMaterialId] = useState<number | null>(null);
  const [calcInkConfigId, setCalcInkConfigId] = useState<number | null>(null);
  const [calcPrintMethod, setCalcPrintMethod] = useState<"SINGLE" | "DOUBLE">("SINGLE"); // 인쇄 방식 (단면/양면)
  const [calcOptions, setCalcOptions] = useState<Record<string, number>>({});

  // Price calc form - 책자 전용
  const [calcPageCount, setCalcPageCount] = useState<number>(0);
  const [calcCoverMaterialId, setCalcCoverMaterialId] = useState<number | null>(null);
  const [calcInnerMaterialId, setCalcInnerMaterialId] = useState<number | null>(null);
  const [calcEndPaperMaterialId, setCalcEndPaperMaterialId] = useState<number | null>(null);
  const [calcInsertCount, setCalcInsertCount] = useState<number>(0);

  // 책자 상품용 용지 선택 (용지 종류 + 평량 분리)
  const [selectedCoverMaterialName, setSelectedCoverMaterialName] = useState<string | null>(null);
  const [selectedCoverGrammage, setSelectedCoverGrammage] = useState<number | null>(null);
  const [selectedInnerMaterialName, setSelectedInnerMaterialName] = useState<string | null>(null);
  const [selectedInnerGrammage, setSelectedInnerGrammage] = useState<number | null>(null);

  // 내지 구간 (내지 커스텀이 켜졌을 때만 사용). 실제 주문 화면과 같이 구간마다 용지·평량·페이지 수를 고른다.
  const [calcInnerSections, setCalcInnerSections] = useState<
    { materialName: string | null; grammage: number | null; pageCount: number }[]
  >([{ materialName: null, grammage: null, pageCount: 0 }]);

  // InkConfig 데이터 (견적식 미리보기용)
  const [inkConfigs, setInkConfigs] = useState<InkConfigDto[]>([]);

  // ProductInkConfig 상태 (상품별 인쇄 구성)
  const [productInkConfigs, setProductInkConfigs] = useState<ProductInkConfigDto[]>([]);
  const [showAddInkConfigModal, setShowAddInkConfigModal] = useState(false);
  const [addingInkConfig, setAddingInkConfig] = useState(false);

  // 동적 용지 옵션 필터링 상태 (견적식 미리보기용)
  const [selectedMaterialName, setSelectedMaterialName] = useState<string | null>(null);
  const [selectedGrammage, setSelectedGrammage] = useState<number | null>(null);
  const [availableOptions, setAvailableOptions] = useState<AvailablePaperOptionsDto | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);

  // 견적식 미리보기 상태
  const [showEstimatePreviewModal, setShowEstimatePreviewModal] = useState(false);
  const [estimateQuantity, setEstimateQuantity] = useState<number>(1);
  const [estimateData, setEstimateData] = useState<EstimatePreviewResponse | null>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState<string | null>(null);

  // ===== Data Loading =====
  const loadInitialData = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const [configData, categoriesData, materialsData, paperSpecsData] = await Promise.all([
        companyProductSystemUseCase.getProductConfig(companyId).catch(() => null),
        companyProductSystemUseCase.getCategories(companyId).catch(() => []),
        companyProductSystemUseCase.getMaterials(companyId).catch(() => []),
        companyProductSystemUseCase.getPaperSpecs(companyId).catch(() => []),
      ]);
      if (configData) setProductConfig(configData);
      setCategories(categoriesData || []);
      setMaterials(materialsData || []);
      setPaperSpecs(paperSpecsData || []);
    } catch (error) {
      console.error("초기 데이터 로드 실패:", error);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const loadProduct = useCallback(async () => {
    if (!companyId || !productId) return;
    try {
      setLoading(true);
      const product = await companyProductSystemUseCase.getProduct(companyId, Number(productId));
      if (product) {
        const productName = product.name || "";
        const productDescription = product.description || "";
        setName(productName);
        setDescription(productDescription);
        setCategoryId(product.categoryId || null);
        setProductType(product.productType || "");
        setPaperType(product.paperType || "");
        setIsProductEnabled((product as any).isEnable !== false); // 기본값 true
        setApplyWasteRate((product as any).applyWasteRate || false);
        setImpositionSettings({
          paperPricingBasis: (product as any).paperPricingBasis === "IMPOSITION" ? "IMPOSITION" : "SIZE_FIT",
          impositionSheetSizeSpecId: (product as any).impositionSheetSizeSpecId ?? null,
          impositionGapMm: (product as any).impositionGapMm ?? 0,
        });
        setIsQuoteOnly((product as any).isQuoteOnly || false);
        setIsSizeCustomizable(product.isSizeCustomizable || false);
        setIsInnerPaperCustomizable((product as any).isInnerPaperCustomizable || false);
        setIsSingleSideEnabled(product.isSingleSideEnabled !== false); // 기본값 true
        setIsDoubleSideEnabled(product.isDoubleSideEnabled !== false); // 기본값 true
        setMinWidth(product.minWidth || null);
        setMaxWidth(product.maxWidth || null);
        setMinHeight(product.minHeight || null);
        setMaxHeight(product.maxHeight || null);
        setThumbnailUrl((product as any).thumbnail || null);
        // 원본 값 저장 (변경 감지용)
        originalValuesRef.current = { name: productName, description: productDescription };
      }
      const [papers, grouped, sizes, opts] = await Promise.all([
        companyProductSystemUseCase.getProductPapers(companyId, Number(productId)).catch(() => []),
        companyProductSystemApiService.getGroupedProductPapers(companyId, Number(productId))
          .then((res) => res.data || [])
          .catch(() => []),
        companyProductSystemUseCase.getSizeTemplates(companyId, Number(productId)).catch(() => []),
        companyProductSystemUseCase.getOptions(companyId, Number(productId)).catch(() => []),
      ]);
      setProductPapers(papers || []);
      setGroupedPapers(grouped || []);
      setSizeTemplates(sizes || []);
      setOptions(opts || []);

      // 커스텀 비허용이고 사이즈 템플릿이 있으면 첫 번째 사이즈로 초기화
      if (product && !product.isSizeCustomizable && sizes && sizes.length > 0) {
        setCalcWidth(sizes[0].width);
        setCalcHeight(sizes[0].height);
      }

      // 각 용지의 sheet paper / roll paper 정보 로드 (지원 용지 표시 및 최대 사이즈 계산용)
      if (papers && papers.length > 0) {
        // SheetPaper 로드
        const sheetPapersPromises = papers.map(async (paper) => {
          try {
            const res = await companyProductSystemApiService.getSheetPapers(companyId, paper.materialId);
            return { materialId: paper.materialId, sheetPapers: res.data || [] };
          } catch {
            return { materialId: paper.materialId, sheetPapers: [] };
          }
        });
        const sheetResults = await Promise.all(sheetPapersPromises);
        const newSheetMap: Record<number, MaterialSheetPaperDto[]> = {};
        sheetResults.forEach(({ materialId, sheetPapers }) => {
          newSheetMap[materialId] = sheetPapers;
        });
        setSheetPapersMap(newSheetMap);

        // RollPaper 로드
        const rollPapersPromises = papers.map(async (paper) => {
          try {
            const res = await companyProductSystemApiService.getRollPapers(companyId, paper.materialId);
            return { materialId: paper.materialId, rollPapers: res.data || [] };
          } catch {
            return { materialId: paper.materialId, rollPapers: [] };
          }
        });
        const rollResults = await Promise.all(rollPapersPromises);
        const newRollMap: Record<number, MaterialRollPaperDto[]> = {};
        rollResults.forEach(({ materialId, rollPapers }) => {
          newRollMap[materialId] = rollPapers;
        });
        setRollPapersMap(newRollMap);
      }

      // InkConfig 로드 (견적식 미리보기용)
      const inkConfigMaterials = materials.filter((m: MaterialDto) => m.type === "INK_CONFIG");
      if (inkConfigMaterials.length > 0) {
        const inkConfigPromises = inkConfigMaterials.map(async (m: MaterialDto) => {
          try {
            const res = await companyProductSystemApiService.getInkConfigs(companyId, m.id);
            return res.data || [];
          } catch {
            return [];
          }
        });
        const allInkConfigs = (await Promise.all(inkConfigPromises)).flat();
        setInkConfigs(allInkConfigs);
      }

      // ProductInkConfig 로드 (상품별 인쇄 구성)
      try {
        const productInkConfigRes = await companyProductSystemApiService.getProductInkConfigs(companyId, Number(productId));
        setProductInkConfigs(productInkConfigRes.data || []);
      } catch {
        setProductInkConfigs([]);
      }
    } catch (error) {
      console.error("상품 로드 실패:", error);
    } finally {
      setLoading(false);
    }
  }, [companyId, productId, materials]);

  useEffect(() => { loadInitialData(); }, [loadInitialData]);
  useEffect(() => {
    if (isEditing) { loadProduct(); }
    else { setShowProductTypeModal(true); }
  }, [isEditing, loadProduct]);

  const loadProductDetailImages = useCallback(async () => {
    if (!companyId || !productId) return;
    try {
      const outputData = await companyProductUseCase.getDetailPages(
        Number(companyId),
        Number(productId),
      );
      setProductDetailImages(outputData);
    } catch (error) {
      // API가 지원되지 않는 상품일 경우 빈 배열 유지
      console.error("상세 이미지 로드 실패:", error);
      setProductDetailImages([]);
    }
  }, [companyId, productId]);

  useEffect(() => {
    if (isEditing) {
      loadProductDetailImages();
    }
  }, [isEditing, loadProductDetailImages]);

  // ===== Thumbnail handlers =====
  const handleThumbnailClick = () => {
    thumbnailInputRef.current?.click();
  };

  const handleThumbnailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !companyId || !productId) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("이미지 파일만 업로드 가능합니다.");
      return;
    }
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("파일 크기는 5MB 이하여야 합니다.");
      return;
    }

    try {
      setThumbnailUploading(true);
      const result = await companyProductSystemUseCase.uploadProductThumbnail(
        companyId,
        Number(productId),
        file,
      );
      if (result?.thumbnail) {
        setThumbnailUrl(result.thumbnail);
      }
    } catch (error) {
      alert("썸네일 업로드에 실패했습니다.");
      console.error(error);
    } finally {
      setThumbnailUploading(false);
    }
  };

  const moveProductDetalPage = async (
    direction: "FORWARD" | "BACKWARD",
    itemId: number,
  ) => {
    if (!companyId || !productId) return;

    const detailPageIds = productDetailImages.map((item) => item.id);
    let foundIndex = detailPageIds.indexOf(itemId);

    if (
      (direction === "BACKWARD" && foundIndex === 0) ||
      (direction === "FORWARD" && foundIndex === detailPageIds.length - 1)
    )
      return;
    let newArray: number[] = [];

    if (direction === "FORWARD") {
      newArray = [
        ...detailPageIds.slice(0, foundIndex),
        ...detailPageIds.slice(foundIndex + 1, foundIndex + 2),
        itemId,
        ...detailPageIds.slice(foundIndex + 2, detailPageIds.length),
      ];
    } else if (direction === "BACKWARD") {
      newArray = [
        ...detailPageIds.slice(0, foundIndex - 1),
        itemId,
        ...detailPageIds.slice(foundIndex - 1, foundIndex),
        ...detailPageIds.slice(foundIndex + 1, detailPageIds.length),
      ];
    }

    if (newArray.length !== detailPageIds.length) return;

    try {
      await companyProductUseCase.updateDetailPageOrders(
        companyId,
        Number(productId),
        newArray,
      );
      await loadProductDetailImages();
    } catch (error) {
      console.error("순서 변경 실패:", error);
      alert("이미지 순서 변경에 실패했습니다.");
    }
  };

  // ===== 변경 감지 =====
  const hasUnsavedChanges = isEditing && (
    name !== originalValuesRef.current.name ||
    description !== originalValuesRef.current.description
  );

  // ===== Product CRUD =====
  const handleSaveProduct = async () => {
    if (!companyId || !name.trim()) return;
    try {
      setSaving(true);
      if (isEditing) {
        const request: UpdateProductRequest = {
          categoryId, name, description, isSizeCustomizable,
          minWidth, maxWidth, minHeight, maxHeight,
        };
        await companyProductSystemUseCase.updateProduct(
          companyId,
          Number(productId),
          request,
        );
        // 저장 성공 후 원본 값 업데이트
        originalValuesRef.current = { name, description };
        addNotificationItem("상품 정보가 저장되었습니다.");
      } else {
        if (!categoryId || !productType || !paperType) {
          alert("카테고리, 상품 타입, 용지 타입을 선택해주세요.");
          return;
        }
        const request: CreateProductRequest = {
          categoryId, name, description,
          productType: productType as any,
          paperType: paperType as any,
          isSizeCustomizable, minWidth, maxWidth, minHeight, maxHeight,
        };
        const created = await companyProductSystemUseCase.createProduct(companyId, request);
        if (created) {
          navigate(`/company/${companyId}/products/${created.id}`, { replace: true });
        }
      }
    } catch (error) {
      alert("저장에 실패했습니다.");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // ===== Size Settings Validation & Save =====
  const validateAndSetSizeErrors = (): boolean => {
    if (!isSizeCustomizable) {
      setSizeValidationErrors({});
      return true;
    }

    const errors: typeof sizeValidationErrors = {};

    if (minWidth !== null && maxWidth !== null && minWidth > maxWidth) {
      errors.minWidth = "최소 가로 값이 최대 가로 값보다 클 수 없습니다.";
      errors.maxWidth = "최소 가로 값이 최대 가로 값보다 클 수 없습니다.";
    }

    if (minHeight !== null && maxHeight !== null && minHeight > maxHeight) {
      errors.minHeight = "최소 세로 값이 최대 세로 값보다 클 수 없습니다.";
      errors.maxHeight = "최소 세로 값이 최대 세로 값보다 클 수 없습니다.";
    }

    setSizeValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveSizeSettings = async (): Promise<boolean> => {
    if (!companyId || !productId) return false;

    const isValid = validateAndSetSizeErrors();

    if (!isValid) {
      return false;
    }

    try {
      setSavingSizeSettings(true);

      // 커스텀 사이즈 설정 모달에서 저장하면 항상 isSizeCustomizable = true
      const request: UpdateProductRequest = {
        isSizeCustomizable: true,
        minWidth,
        maxWidth,
        minHeight,
        maxHeight,
      };

      await companyProductSystemUseCase.updateProduct(
        companyId,
        Number(productId),
        request,
      );

      // 저장 성공 후 상태 업데이트
      setIsSizeCustomizable(true);
      addNotificationItem("사이즈 설정이 저장되었습니다.");
      return true;
    } catch (error) {
      console.error("사이즈 설정 저장 실패:", error);
      addNotificationItem("사이즈 설정 저장에 실패했습니다.");
      return false;
    } finally {
      setSavingSizeSettings(false);
    }
  };

  // ===== Paper CRUD =====
  const handleAddPaper = async () => {
    if (!companyId || !productId || !selectedMaterialId) return;
    try {
      const request: CreateProductPaperRequest = {
        materialId: selectedMaterialId,
        paperUsageType: selectedPaperUsageType,
        isSizeCustomizable: false,
      };
      await companyProductSystemUseCase.createProductPaper(companyId, Number(productId), request);
      const [papers, grouped] = await Promise.all([
        companyProductSystemUseCase.getProductPapers(companyId, Number(productId)),
        companyProductSystemApiService.getGroupedProductPapers(companyId, Number(productId))
          .then((res) => res.data || []),
      ]);
      setProductPapers(papers || []);
      setGroupedPapers(grouped || []);
      setShowAddPaperModal(false);
      setSelectedMaterialId(null);
      setPaperAddStep(1);
      setSelectedPaperBaseName("");
      setSelectedPaperUsageType("GENERAL");
    } catch (error) { alert(resolveErrorMessage(error, "용지 추가에 실패했습니다.")); }
  };

  const handleDeletePaper = async (paperId: number) => {
    if (!companyId || !productId) return;
    if (!window.confirm("이 용지를 삭제하시겠습니까?")) return;
    try {
      await companyProductSystemUseCase.deleteProductPaper(companyId, Number(productId), paperId);
      setProductPapers((prev) => prev.filter((p) => p.id !== paperId));
      // 그룹핑 데이터 갱신
      const grouped = await companyProductSystemApiService.getGroupedProductPapers(companyId, Number(productId))
        .then((res) => res.data || []);
      setGroupedPapers(grouped);
    } catch (error) { alert("용지 삭제에 실패했습니다."); }
  };

  // ===== Size CRUD =====
  /** 서버가 내려준 실패 사유를 꺼낸다. 없으면 fallback */
  const resolveErrorMessage = (error: unknown, fallback: string): string => {
    const original = (error as { originalError?: unknown })?.originalError;
    const message = original instanceof Error ? original.message : error instanceof Error ? error.message : null;
    return message?.trim() || fallback;
  };

  const handleAddSize = async () => {
    if (!companyId || !productId || !newSizeWidth || !newSizeHeight) return;
    try {
      await companyProductSystemUseCase.createSizeTemplate(companyId, Number(productId), {
        width: newSizeWidth,
        height: newSizeHeight,
        name: newSizeName.trim() || null,
      } as CreateSizeTemplateRequest);
      const sizes = await companyProductSystemUseCase.getSizeTemplates(companyId, Number(productId));
      setSizeTemplates(sizes || []);
      setShowAddSizeModal(false);
      setNewSizeWidth(0);
      setNewSizeHeight(0);
      setNewSizeName("");
    } catch (error) { alert(resolveErrorMessage(error, "사이즈 추가에 실패했습니다.")); }
  };

  const handleDeleteSize = async (sizeId: number) => {
    if (!companyId || !productId) return;
    if (!window.confirm("이 사이즈를 삭제하시겠습니까?")) return;
    try {
      await companyProductSystemUseCase.deleteSizeTemplate(companyId, Number(productId), sizeId);
      setSizeTemplates((prev) => prev.filter((s) => s.id !== sizeId));
    } catch (error) { alert("사이즈 삭제에 실패했습니다."); }
  };

  const handleEditSize = (size: SizeTemplateDto) => {
    setEditingSizeId(size.id);
    setEditSizeWidth(size.width);
    setEditSizeHeight(size.height);
    setEditSizeName(size.name || "");
    setShowEditSizeModal(true);
  };

  const handleUpdateSize = async () => {
    if (!companyId || !productId || !editingSizeId || !editSizeWidth || !editSizeHeight) return;
    try {
      await companyProductSystemUseCase.updateSizeTemplate(
        companyId,
        Number(productId),
        editingSizeId,
        { width: editSizeWidth, height: editSizeHeight, name: editSizeName.trim() || null }
      );
      const sizes = await companyProductSystemUseCase.getSizeTemplates(companyId, Number(productId));
      setSizeTemplates(sizes || []);
      setShowEditSizeModal(false);
      setEditingSizeId(null);
      setEditSizeName("");
      addNotificationItem("사이즈가 수정되었습니다.");
    } catch (error) {
      alert(resolveErrorMessage(error, "사이즈 수정에 실패했습니다."));
    }
  };

  // ===== Option helpers =====
  const refreshOptions = async () => {
    if (!companyId || !productId) return;
    const opts = await companyProductSystemUseCase.getOptions(companyId, Number(productId));
    setOptions(opts || []);
  };

  const handleAddOption = async () => {
    if (!companyId || !productId || !newOptionName.trim()) return;
    try {
      const request: CreateOptionRequest = {
        name: newOptionName,
        description: newOptionDescription || undefined,
        quantityType: DEFAULT_NEW_OPTION_QUANTITY_TYPE,
      };
      await companyProductSystemUseCase.createOption(companyId, Number(productId), request);
      await refreshOptions();
      setShowAddOptionModal(false);
      setNewOptionName("");
      setNewOptionDescription("");
    } catch (error) { alert("옵션 추가에 실패했습니다."); }
  };

  const handleAddPredefinedOption = async (template: PredefinedOptionTemplate) => {
    if (!companyId || !productId) return;
    try {
      if (template.isSpecialEndpoint) {
        if (template.key === "insert_paper") {
          await companyProductSystemUseCase.addInsertPaper(companyId, Number(productId));
        } else if (template.key === "paper_orientation") {
          await companyProductSystemUseCase.addPaperOrientation(companyId, Number(productId));
        }
      } else {
        const request: CreateOptionRequest = {
          name: template.name,
          optionType: template.optionType as any,
          quantityType: DEFAULT_NEW_OPTION_QUANTITY_TYPE,
        };
        await companyProductSystemUseCase.createOption(companyId, Number(productId), request);
      }
      await refreshOptions();
      setShowOptionSelectModal(false);
    } catch (error) { alert("옵션 추가에 실패했습니다."); }
  };

  const handleDeleteOption = async (optionId: number) => {
    if (!companyId || !productId) return;
    if (!window.confirm("이 옵션을 삭제하시겠습니까?")) return;
    try {
      await companyProductSystemUseCase.deleteOption(companyId, Number(productId), optionId);
      setOptions((prev) => prev.filter((o) => o.id !== optionId));
    } catch (error) { alert("옵션 삭제에 실패했습니다."); }
  };

  // ===== ProductInkConfig (상품별 인쇄 구성) =====
  const handleAddProductInkConfig = async (inkConfigId: number) => {
    if (!companyId || !productId) return;
    try {
      setAddingInkConfig(true);
      const res = await companyProductSystemApiService.createProductInkConfig(
        companyId,
        Number(productId),
        inkConfigId
      );
      if (res.data) {
        setProductInkConfigs((prev) => [...prev, res.data!]);
        setShowAddInkConfigModal(false);
        addNotificationItem("인쇄 구성이 추가되었습니다.");
      }
    } catch (error: any) {
      alert(error.message || "인쇄 구성 추가에 실패했습니다.");
    } finally {
      setAddingInkConfig(false);
    }
  };

  const handleDeleteProductInkConfig = async (productInkConfigId: number) => {
    if (!companyId || !productId) return;
    if (!window.confirm("이 인쇄 구성을 삭제하시겠습니까?")) return;
    try {
      await companyProductSystemApiService.deleteProductInkConfig(
        companyId,
        Number(productId),
        productInkConfigId
      );
      setProductInkConfigs((prev) => prev.filter((c) => c.id !== productInkConfigId));
      addNotificationItem("인쇄 구성이 삭제되었습니다.");
    } catch (error) {
      alert("인쇄 구성 삭제에 실패했습니다.");
    }
  };

  // 모달에서 선택 가능한 InkConfig (이미 추가된 것 제외)
  const availableInkConfigsForModal = inkConfigs.filter(
    (ic) => !productInkConfigs.some((pic) => pic.inkConfigId === ic.id)
  );

  /**
   * 내지 구간 분할 허용 여부를 토글한다.
   *
   * 켜면 고객이 내지를 구간으로 나눠(구간마다 용지·평량·페이지 수) 주문할 수 있고,
   * 끄면 예전처럼 내지 용지 하나 + 총 페이지 수만 고르게 된다.
   */
  const handleToggleInnerPaperCustomizable = async () => {
    if (!companyId || !productId) return;

    const next = !isInnerPaperCustomizable;
    try {
      await companyProductSystemUseCase.updateProduct(companyId, Number(productId), {
        isInnerPaperCustomizable: next,
      });
      setIsInnerPaperCustomizable(next);
      addNotificationItem(next ? "내지 구간 설정을 허용했습니다." : "내지 구간 설정을 비허용으로 바꿨습니다.");
    } catch {
      addNotificationItem("설정 저장에 실패했습니다.");
    }
  };

  /**
   * 쇼핑몰에서 고를 수 있는 인쇄 방식(단면/양면)을 켜고 끈다.
   *
   * 인쇄 구성처럼 허용한 것만 쇼핑몰에 보인다. 둘 다 끄면 주문할 수 없으므로 하나는 남긴다.
   */
  const handleTogglePrintSide = async (side: "SINGLE" | "DOUBLE") => {
    if (!companyId || !productId) return;

    const nextSingle = side === "SINGLE" ? !isSingleSideEnabled : isSingleSideEnabled;
    const nextDouble = side === "DOUBLE" ? !isDoubleSideEnabled : isDoubleSideEnabled;
    if (!nextSingle && !nextDouble) {
      addNotificationItem("인쇄 방식은 단면·양면 중 하나 이상 허용해야 합니다.");
      return;
    }
    try {
      await companyProductSystemUseCase.updateProduct(companyId, Number(productId), {
        isSingleSideEnabled: nextSingle,
        isDoubleSideEnabled: nextDouble,
      });
      setIsSingleSideEnabled(nextSingle);
      setIsDoubleSideEnabled(nextDouble);
      const label = side === "SINGLE" ? "단면" : "양면";
      const enabled = side === "SINGLE" ? nextSingle : nextDouble;
      addNotificationItem(enabled ? `${label} 인쇄를 허용했습니다.` : `${label} 인쇄를 쇼핑몰에서 숨겼습니다.`);
    } catch {
      addNotificationItem("설정 저장에 실패했습니다.");
    }
  };

  // ===== Price Calculation =====
  const handleCalculatePrice = async () => {
    if (!companyId || !productId) return;
    try {
      const isBook = isBookProductType(productType);
      // 내지 커스텀이 켜진 책자는 구간별 용지·페이지 수로 계산한다 (총 페이지 수 = 구간 합계)
      const innerSections = isBook && isInnerPaperCustomizable ? buildInnerPaperSections() : [];
      const useInnerSections = innerSections.length > 0;

      const request: PriceCalculationRequest = {
        width: calcWidth,
        height: calcHeight,
        quantity: calcQuantity,
        // 일반 상품용
        materialId: isBook ? null : calcMaterialId,
        inkConfigId: calcInkConfigId,
        options: Object.keys(calcOptions).length > 0 ? calcOptions : undefined,
        isDoubleSided: calcPrintMethod === "DOUBLE",  // 양면: true, 단면: false
        // 책자 상품용
        pageCount: isBook
          ? (useInnerSections ? innerSections.reduce((sum, s) => sum + s.pageCount, 0) : calcPageCount)
          : undefined,
        coverMaterialId: isBook ? calcCoverMaterialId : undefined,
        innerMaterialId: isBook && !useInnerSections ? calcInnerMaterialId : undefined,
        innerPaperSections: useInnerSections ? innerSections : undefined,
        endPaperMaterialId: productType === "HARD_COVER_BOOK" ? calcEndPaperMaterialId : undefined,
        insertCount: isBook && calcInsertCount > 0 ? calcInsertCount : undefined,
      };
      const result = await companyProductSystemUseCase.calculatePrice(companyId, Number(productId), request);
      setPriceResult(result);
    } catch (error: unknown) {
      // 백엔드 에러 메시지 추출
      const err = error as { response?: { data?: { message?: string } } };
      const message = err.response?.data?.message || "가격 계산에 실패했습니다.";
      alert(message);
    }
  };

  // ===== 견적식 미리보기 로드 =====
  const loadEstimatePreview = useCallback(async (quantity: number = 1) => {
    if (!companyId || !productId) return;
    try {
      setEstimateLoading(true);
      setEstimateError(null);
      const response = await companyProductSystemApiService.getEstimatePreview(
        companyId,
        Number(productId),
        quantity
      );
      setEstimateData(response.data);
    } catch (error: unknown) {
      const err = error as { message?: string };
      setEstimateError(err.message || "견적식 미리보기를 불러오지 못했습니다.");
      setEstimateData(null);
    } finally {
      setEstimateLoading(false);
    }
  }, [companyId, productId]);

  // 모달 열릴 때 또는 수량 변경 시 견적식 미리보기 로드
  useEffect(() => {
    if (showEstimatePreviewModal && groupedPapers.length > 0 && sizeTemplates.length > 0) {
      loadEstimatePreview(estimateQuantity);
    }
  }, [showEstimatePreviewModal, groupedPapers.length, sizeTemplates.length, estimateQuantity, loadEstimatePreview]);

  // ===== 동적 용지 옵션 필터링 =====

  /**
   * 사용 가능한 용지 옵션 로드
   * - 선택된 용지 종류/평량에 따라 상호 필터링된 옵션 목록을 가져옵니다
   */
  const loadAvailableOptions = useCallback(async (
    materialName: string | null,
    grammage: number | null
  ) => {
    if (!companyId || !productId) return;
    try {
      setOptionsLoading(true);
      const result = await companyProductSystemApiService.getAvailablePaperOptions(
        companyId,
        Number(productId),
        "GENERAL", // 일반 상품용
        materialName || undefined,
        grammage || undefined
      );
      setAvailableOptions(result.data);

      // 둘 다 선택 시 매칭되는 Material ID 설정
      if (result.data?.matchingMaterialId) {
        setCalcMaterialId(result.data.matchingMaterialId);
      }
    } catch (error) {
      console.error("용지 옵션 로드 실패:", error);
    } finally {
      setOptionsLoading(false);
    }
  }, [companyId, productId]);

  /**
   * 용지 종류 선택 핸들러
   */
  const handleMaterialNameSelect = useCallback((name: string) => {
    if (selectedMaterialName === name) {
      // 이미 선택된 용지를 다시 클릭하면 선택 해제
      setSelectedMaterialName(null);
      setCalcMaterialId(null);
      loadAvailableOptions(null, selectedGrammage);
    } else {
      setSelectedMaterialName(name);
      loadAvailableOptions(name, selectedGrammage);
    }
  }, [selectedMaterialName, selectedGrammage, loadAvailableOptions]);

  /**
   * 평량 선택 핸들러
   */
  const handleGrammageSelect = useCallback((grammage: number) => {
    if (selectedGrammage === grammage) {
      // 이미 선택된 평량을 다시 클릭하면 선택 해제
      setSelectedGrammage(null);
      setCalcMaterialId(null);
      loadAvailableOptions(selectedMaterialName, null);
    } else {
      setSelectedGrammage(grammage);
      loadAvailableOptions(selectedMaterialName, grammage);
    }
  }, [selectedMaterialName, selectedGrammage, loadAvailableOptions]);

  /**
   * 견적식 미리보기 초기화 시 사용 가능한 옵션 로드
   */
  useEffect(() => {
    if (companyId && productId && groupedPapers.length > 0 && !isBookProductType(productType)) {
      loadAvailableOptions(null, null);
    }
  }, [companyId, productId, groupedPapers.length, productType, loadAvailableOptions]);

  // 책자 상품: 표지 용지 종류 + 평량 선택 시 materialId 자동 계산
  React.useEffect(() => {
    if (!isBookProductType(productType)) return;
    if (!selectedCoverMaterialName || !selectedCoverGrammage) {
      setCalcCoverMaterialId(null);
      return;
    }

    // groupedPapers에서 표지 용지 그룹 찾기
    const coverGroup = groupedPapers.find(
      (g) => g.paperUsageType === "COVER" && g.materialName === selectedCoverMaterialName
    );
    if (!coverGroup) {
      setCalcCoverMaterialId(null);
      return;
    }

    // 해당 평량의 인덱스 찾기
    const grammageIndex = coverGroup.grammages.indexOf(selectedCoverGrammage);
    if (grammageIndex === -1) {
      setCalcCoverMaterialId(null);
      return;
    }

    // paperId로 productPapers에서 materialId 찾기
    const paperId = coverGroup.paperIds[grammageIndex];
    const productPaper = productPapers.find((pp) => pp.id === paperId);
    if (productPaper) {
      setCalcCoverMaterialId(productPaper.materialId);
    } else {
      setCalcCoverMaterialId(null);
    }
  }, [productType, selectedCoverMaterialName, selectedCoverGrammage, groupedPapers, productPapers]);

  /**
   * 내지 용지 종류 + 평량 조합의 materialId를 찾는다.
   *
   * groupedPapers는 용지명으로 묶여 있고 평량과 paperId가 같은 순서로 들어 있어,
   * 평량 인덱스로 paperId를 고른 뒤 productPapers에서 materialId를 얻는다.
   * 단일 내지 선택과 내지 구간 모두 같은 규칙을 쓴다.
   */
  const resolveInnerMaterialId = useCallback(
    (materialName: string | null, grammage: number | null): number | null => {
      if (!materialName || !grammage) return null;

      const innerGroup = groupedPapers.find(
        (g) => g.paperUsageType === "INNER" && g.materialName === materialName
      );
      if (!innerGroup) return null;

      const grammageIndex = innerGroup.grammages.indexOf(grammage);
      if (grammageIndex === -1) return null;

      const paperId = innerGroup.paperIds[grammageIndex];
      return productPapers.find((pp) => pp.id === paperId)?.materialId ?? null;
    },
    [groupedPapers, productPapers]
  );

  // 책자 상품: 내지 용지 종류 + 평량 선택 시 materialId 자동 계산
  React.useEffect(() => {
    if (!isBookProductType(productType)) return;
    setCalcInnerMaterialId(resolveInnerMaterialId(selectedInnerMaterialName, selectedInnerGrammage));
  }, [productType, selectedInnerMaterialName, selectedInnerGrammage, resolveInnerMaterialId]);

  /** 미리보기에서 고른 내지 구간을 계산 요청 형식으로 바꾼다 (용지·페이지 수가 모두 채워진 구간만) */
  const buildInnerPaperSections = useCallback(() => {
    return calcInnerSections
      .map((section) => ({
        materialId: resolveInnerMaterialId(section.materialName, section.grammage),
        pageCount: section.pageCount,
      }))
      .filter((section): section is { materialId: number; pageCount: number } =>
        section.materialId !== null && section.pageCount > 0
      );
  }, [calcInnerSections, resolveInnerMaterialId]);

  /** 내지 구간의 총 페이지 수 (구간 모드에서 페이지 수는 구간 합계다) */
  const innerSectionsTotalPageCount = calcInnerSections.reduce(
    (sum, section) => sum + (section.pageCount || 0),
    0
  );

  // ===== Helpers =====

  /**
   * 미리보기 옵션의 조건부 표시 상태
   *
   * 실제 주문 화면과 같은 규칙으로 옵션·속성·값의 노출 여부를 계산한다.
   * 조건에서 벗어난 선택은 effectiveSelection에서 빠지므로 가격 계산에도 반영된다.
   */
  const optionVisibility = React.useMemo(
    () => resolveOptionVisibility(options, calcOptions),
    [options, calcOptions],
  );

  // 조건에서 벗어난 선택이 남아 있으면 상태에서도 정리한다 (가격 계산과 화면을 일치시킴)
  React.useEffect(() => {
    const next = optionVisibility.effectiveSelection;
    const changed =
      Object.keys(next).length !== Object.keys(calcOptions).length ||
      Object.entries(next).some(([key, value]) => calcOptions[key] !== value);
    if (changed) setCalcOptions(next);
  }, [optionVisibility, calcOptions]);

  // 용지 기본 이름 추출 (평량 제거): "아트지 180g" → "아트지"
  const extractBaseName = (name: string): string => {
    return name.replace(/\s*\(?\d+g\)?$/i, "").trim();
  };

  // 필터링된 자재 (용지 타입만)
  // 책자 표지·면지는 롤 단가가 없어 상품 용지 타입과 무관하게 낱장 용지만 허용한다 (백엔드도 같은 규칙으로 거부)
  const filteredMaterials = React.useMemo(() => {
    return materials.filter((m) => {
      if (isBookProductType(productType) && selectedPaperUsageType === "COVER") return m.type === "SHEET_PAPER";
      if (selectedPaperUsageType === "END_PAPER") return m.type === "SHEET_PAPER";
      if (!paperType) return m.type === "SHEET_PAPER" || m.type === "ROLL_PAPER";
      return paperType === "SHEET_PAPER" ? m.type === "SHEET_PAPER" : m.type === "ROLL_PAPER";
    });
  }, [materials, paperType, productType, selectedPaperUsageType]);

  // 자재를 용지 종류별로 그룹핑 (기본 이름 → Material[])
  const materialGroups = React.useMemo(() => {
    const groups: Record<string, typeof filteredMaterials> = {};
    filteredMaterials.forEach((m) => {
      const baseName = extractBaseName(m.name);
      if (!groups[baseName]) groups[baseName] = [];
      groups[baseName].push(m);
    });
    // 각 그룹 내에서 이름순 정렬
    Object.values(groups).forEach((mats) => mats.sort((a, b) => a.name.localeCompare(b.name)));
    return groups;
  }, [filteredMaterials]);

  // 선택된 용지 종류의 자재 목록
  const availableMaterialsForGroup = selectedPaperBaseName ? materialGroups[selectedPaperBaseName] || [] : [];

  const getMaterialName = (materialId: number) =>
    materials.find((m) => m.id === materialId)?.name || `자재 #${materialId}`;

  const getMaterialSpec = (materialId: number): PaperSpecDto | undefined => {
    const material = materials.find((m) => m.id === materialId);
    if (!material?.paperSpecId) return undefined;
    return paperSpecs.find((s) => s.id === material.paperSpecId);
  };


  // 해당 자재의 가장 큰 용지 사이즈 가져오기
  const getMaxSheetSize = (materialId: number): { width: number; height: number } | null => {
    const sheetPapers = sheetPapersMap[materialId];
    if (!sheetPapers || sheetPapers.length === 0) return null;

    // 면적 기준으로 가장 큰 용지 찾기
    let maxPaper = sheetPapers[0];
    let maxArea = maxPaper.width * maxPaper.height;

    sheetPapers.forEach((sp) => {
      const area = sp.width * sp.height;
      if (area > maxArea) {
        maxArea = area;
        maxPaper = sp;
      }
    });

    return { width: maxPaper.width, height: maxPaper.height };
  };

  // 특정 사이즈를 인쇄할 수 있는 용지 목록 가져오기
  const getSupportingPapers = (sizeWidth: number, sizeHeight: number): string[] => {
    const supportingPapers: string[] = [];

    productPapers.forEach((paper) => {
      let canPrint = false;

      // SheetPaper 확인
      const sheetPapers = sheetPapersMap[paper.materialId];
      if (sheetPapers && sheetPapers.length > 0) {
        canPrint = sheetPapers.some((sp) => {
          // 정방향 또는 회전하여 인쇄 가능한지 확인
          return (sp.width >= sizeWidth && sp.height >= sizeHeight) ||
                 (sp.width >= sizeHeight && sp.height >= sizeWidth);
        });
      }

      // RollPaper 확인 (SheetPaper에서 지원하지 않는 경우)
      if (!canPrint) {
        const rollPapers = rollPapersMap[paper.materialId];
        if (rollPapers && rollPapers.length > 0) {
          // RollPaper는 width(롤 폭)만 확인 - height는 무제한
          canPrint = rollPapers.some((rp) => rp.width >= sizeWidth);
        }
      }

      if (canPrint) {
        const materialName = getMaterialName(paper.materialId);
        if (!supportingPapers.includes(materialName)) {
          supportingPapers.push(materialName);
        }
      }
    });

    return supportingPapers;
  };

  // 용지 관리에서 추가된 용지들의 최대 폭(width) 계산
  // 커스텀 사이즈 허용 시 최대 가로 제한에 사용
  const getMaxPaperWidth = (): number | null => {
    let maxWidth = 0;

    productPapers.forEach((paper) => {
      // SheetPaper에서 최대 width 확인
      const sheetPapers = sheetPapersMap[paper.materialId];
      if (sheetPapers && sheetPapers.length > 0) {
        sheetPapers.forEach((sp) => {
          // SheetPaper는 회전 가능하므로 width와 height 중 큰 값 사용
          const paperMaxDim = Math.max(sp.width, sp.height);
          if (paperMaxDim > maxWidth) maxWidth = paperMaxDim;
        });
      }

      // RollPaper에서 최대 width 확인
      const rollPapers = rollPapersMap[paper.materialId];
      if (rollPapers && rollPapers.length > 0) {
        rollPapers.forEach((rp) => {
          if (rp.width > maxWidth) maxWidth = rp.width;
        });
      }
    });

    return maxWidth > 0 ? maxWidth : null;
  };

  const handleProductTypeSelect = (type: string) => {
    setProductType(type);
    setShowProductTypeModal(false);
    if (isRollPaperProductType(type)) { setPaperType("ROLL_PAPER"); }
    else { setShowPaperTypeModal(true); }
  };

  const handlePaperTypeSelect = (type: string) => {
    setPaperType(type);
    setShowPaperTypeModal(false);
  };

  // ===== Loading =====
  if (loading) {
    return (
      <CompanyAdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-[14px] text-[#999]">불러오는 중...</p>
        </div>
      </CompanyAdminLayout>
    );
  }

  // Inline styles for modal inputs (consistent)
  const modalInput = "w-full border border-[#e0e0e0] rounded-[10px] px-4 py-3 text-[14px] text-[#333] placeholder-[#bbb] focus:border-[#4285F4] focus:outline-none transition-colors";

  return (
    <>
      <NotificationsList notifications={notifications} />

      {/* ===== 상품 타입 선택 모달 (Frame 291045 기반) ===== */}
      {showProductTypeModal && (
        <Modal setIsModalVisible={setShowProductTypeModal} title="">
          <div className="w-[70vw] max-w-[520px] text-center py-2">
            <h2 className="text-[18px] font-bold text-[#222] mb-6">추가할 상품을 선택해주세요</h2>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-4 mb-6">
              {(productConfig?.productTypes || Object.keys(PRODUCT_TYPE_LABELS)).map((type) => (
                <button
                  key={type}
                  onClick={() => handleProductTypeSelect(type)}
                  className="flex flex-col items-center gap-2 group"
                >
                  <div className="w-[72px] h-[72px] bg-[#f0f0f0] rounded-[14px] group-hover:bg-[#E8EFFF] transition-colors" />
                  <span className="text-[12px] font-medium text-[#555]">{PRODUCT_TYPE_LABELS[type] || type}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => { setShowProductTypeModal(false); navigate(-1); }}
                className="px-5 py-2.5 bg-[#222] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#444] transition-colors"
              >
                취소
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ===== 금액 결정 방식 선택 모달 (Frame 291046 기반) ===== */}
      {showPaperTypeModal && (
        <Modal setIsModalVisible={setShowPaperTypeModal} title="">
          <div className="w-[70vw] max-w-[520px] text-center py-2">
            <h2 className="text-[18px] font-bold text-[#222] mb-6">상품의 금액 결정 방식을 선택해주세요</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* 회배당 금액 */}
              <button
                onClick={() => handlePaperTypeSelect("ROLL_PAPER")}
                className="border-2 border-[#e0e0e0] rounded-[14px] p-5 hover:border-[#4285F4] transition-colors text-center group"
              >
                <div className="w-full h-[100px] mb-3 flex items-center justify-center">
                  <img src="/assets/images/plotter.png" alt="회배당 금액" className="h-[80px] object-contain" />
                </div>
                <p className="text-[15px] font-bold text-[#222] mb-1">회배당 금액</p>
                <p className="text-[12px] text-[#888] leading-relaxed">
                  종이의 1m²(회배) 당 금액으로<br />상품의 금액이 결정됩니다
                </p>
              </button>
              {/* 종이 규격 금액 */}
              <button
                onClick={() => handlePaperTypeSelect("SHEET_PAPER")}
                className="border-2 border-[#e0e0e0] rounded-[14px] p-5 hover:border-[#4285F4] transition-colors text-center group"
              >
                <div className="w-full h-[100px] mb-3 flex items-center justify-center">
                  <img src="/assets/images/printer.png" alt="종이 규격 금액" className="h-[80px] object-contain" />
                </div>
                <p className="text-[15px] font-bold text-[#222] mb-1">종이 규격 금액</p>
                <p className="text-[12px] text-[#888] leading-relaxed">
                  입력한 크기를 출력할 수 있는 최소 종이<br />규격을 찾아 금액이 결정됩니다
                </p>
              </button>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => handlePaperTypeSelect("")}
                className="px-5 py-2.5 bg-[#222] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#444] transition-colors"
              >
                확인
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ===== 용지 추가 모달 (2단계) ===== */}
      {showAddPaperModal && (
        <Modal
          setIsModalVisible={(val) => {
            setShowAddPaperModal(val);
            if (!val) {
              setPaperAddStep(1);
              setSelectedPaperBaseName("");
            }
          }}
          // 롤 용지(현수막 원단 등)는 평량으로 구분하지 않으므로 원단 이름으로 고른다
          title={paperAddStep === 1 ? "용지 종류 선택" : paperType === "ROLL_PAPER" ? "원단 선택" : "평량/자재 선택"}
        >
          <div className="w-[70vw] max-w-[420px]">
            {paperAddStep === 1 ? (
              /* Step 1: 용지 종류 선택 */
              <>
                <label className="block text-[12px] font-medium text-[#555] mb-2">용지 종류 선택</label>
                {Object.keys(materialGroups).length === 0 ? (
                  <p className="text-[12px] text-[#999]">등록된 용지 자재가 없습니다.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-[280px] overflow-y-auto">
                    {Object.entries(materialGroups).map(([baseName, mats]) => (
                      <button
                        key={baseName}
                        onClick={() => {
                          setSelectedPaperBaseName(baseName);
                          setPaperAddStep(2);
                          // 자재가 하나만 있으면 자동 선택
                          if (mats.length === 1) {
                            setSelectedMaterialId(mats[0].id);
                          }
                        }}
                        className="flex flex-col items-start p-3 border border-[#e0e0e0] rounded-[10px] hover:border-[#4285F4] hover:bg-[#F5F8FF] transition-colors text-left"
                      >
                        <span className="text-[14px] font-medium text-[#333] mb-1">{baseName}</span>
                        <span className="text-[11px] text-[#888]">
                          {mats.map((m) => {
                            const spec = paperSpecs.find((ps) => ps.id === m.paperSpecId);
                            return spec && paperType !== "ROLL_PAPER" ? `${spec.grammage}g` : m.name;
                          }).join(", ")}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* Step 2: 평량/자재 선택 */
              <>
                <div className="mb-3">
                  <span className="text-[14px] font-medium text-[#333]">{selectedPaperBaseName}</span>
                </div>
                <label className="block text-[12px] font-medium text-[#555] mb-2">{paperType === "ROLL_PAPER" ? "원단 선택" : "평량 선택"}</label>
                <div className="grid grid-cols-3 gap-2">
                  {availableMaterialsForGroup.map((m) => {
                    const spec = paperType === "ROLL_PAPER" ? undefined : paperSpecs.find((ps) => ps.id === m.paperSpecId);
                    return (
                      <button
                        key={m.id}
                        onClick={() => setSelectedMaterialId(m.id)}
                        className={`p-3 border rounded-[10px] transition-colors text-center ${
                          selectedMaterialId === m.id
                            ? "border-[#4285F4] bg-[#E8EFFF] text-[#4285F4]"
                            : "border-[#e0e0e0] hover:border-[#4285F4] hover:bg-[#F5F8FF] text-[#333]"
                        }`}
                      >
                        <span className="text-[15px] font-bold">{spec ? `${spec.grammage}g` : m.name}</span>
                        {spec && <p className="text-[10px] text-[#888] mt-0.5">{spec.thicknessMm}mm</p>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => {
                  setShowAddPaperModal(false);
                  setPaperAddStep(1);
                  setSelectedPaperBaseName("");
                }}
                className="px-4 py-2.5 text-[13px] text-[#666] hover:text-[#333] transition-colors"
              >
                취소
              </button>
              {paperAddStep === 2 && (
                <button
                  onClick={handleAddPaper}
                  disabled={!selectedMaterialId}
                  className="px-5 py-2.5 bg-[#222] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#444] disabled:bg-[#e0e0e0] disabled:text-[#aaa] transition-colors"
                >
                  추가
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* ===== 사이즈 추가 모달 ===== */}
      {showAddSizeModal && (
        <Modal setIsModalVisible={setShowAddSizeModal} title="사이즈 템플릿 추가">
          <div className="w-[70vw] max-w-[400px]">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[13px] font-medium text-[#555] mb-2">가로 (mm)</label>
                <input type="number" className={modalInput} value={newSizeWidth || ""} onChange={(e) => setNewSizeWidth(Number(e.target.value))} placeholder="90" />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#555] mb-2">세로 (mm)</label>
                <input type="number" className={modalInput} value={newSizeHeight || ""} onChange={(e) => setNewSizeHeight(Number(e.target.value))} placeholder="50" />
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-[13px] font-medium text-[#555] mb-2">이름 (선택)</label>
              <input
                type="text"
                className={modalInput}
                value={newSizeName}
                maxLength={50}
                onChange={(e) => setNewSizeName(e.target.value)}
                placeholder={newSizeWidth && newSizeHeight ? `${newSizeWidth}×${newSizeHeight}` : "예: A4, 명함"}
              />
              <p className="text-[11px] text-[#888] mt-1.5">
                입력하지 않으면 "{newSizeWidth || 0}×{newSizeHeight || 0}" 형태로 표시됩니다
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowAddSizeModal(false)} className="px-4 py-2.5 text-[13px] text-[#666] hover:text-[#333] transition-colors">취소</button>
              <button onClick={handleAddSize} disabled={!newSizeWidth || !newSizeHeight} className="px-5 py-2.5 bg-[#222] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#444] disabled:bg-[#e0e0e0] disabled:text-[#aaa] transition-colors">추가</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ===== 사이즈 수정 모달 ===== */}
      {showEditSizeModal && (
        <Modal setIsModalVisible={setShowEditSizeModal} title="사이즈 수정">
          <div className="w-[70vw] max-w-[400px]">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[13px] font-medium text-[#555] mb-2">가로 (mm)</label>
                <input type="number" className={modalInput} value={editSizeWidth || ""} onChange={(e) => setEditSizeWidth(Number(e.target.value))} placeholder="90" />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#555] mb-2">세로 (mm)</label>
                <input type="number" className={modalInput} value={editSizeHeight || ""} onChange={(e) => setEditSizeHeight(Number(e.target.value))} placeholder="50" />
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-[13px] font-medium text-[#555] mb-2">이름 (선택)</label>
              <input
                type="text"
                className={modalInput}
                value={editSizeName}
                maxLength={50}
                onChange={(e) => setEditSizeName(e.target.value)}
                placeholder={editSizeWidth && editSizeHeight ? `${editSizeWidth}×${editSizeHeight}` : "예: A4, 명함"}
              />
              <p className="text-[11px] text-[#888] mt-1.5">
                입력하지 않으면 "{editSizeWidth || 0}×{editSizeHeight || 0}" 형태로 표시됩니다
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowEditSizeModal(false)} className="px-4 py-2.5 text-[13px] text-[#666] hover:text-[#333] transition-colors">취소</button>
              <button onClick={handleUpdateSize} disabled={!editSizeWidth || !editSizeHeight} className="px-5 py-2.5 bg-[#222] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#444] disabled:bg-[#e0e0e0] disabled:text-[#aaa] transition-colors">저장</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ===== 인쇄 구성 추가 모달 ===== */}
      {showAddInkConfigModal && (
        <Modal setIsModalVisible={setShowAddInkConfigModal} title="인쇄 구성 추가">
          <div className="w-[70vw] max-w-[400px]">
            <p className="text-[13px] text-[#666] mb-4">
              추가할 인쇄 구성을 선택하세요
            </p>
            {availableInkConfigsForModal.length === 0 ? (
              <p className="text-[14px] text-[#bbb] py-8 text-center">
                추가 가능한 인쇄 구성이 없습니다
              </p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto mb-4">
                {availableInkConfigsForModal.map((inkConfig) => {
                  // 가장 낮은 수량 구간의 가격 (대표 가격)
                  const basePrice = inkConfig.priceTiers.length > 0
                    ? inkConfig.priceTiers.reduce((min, tier) =>
                        tier.minQuantity < min.minQuantity ? tier : min
                      ).price
                    : null;

                  return (
                    <button
                      key={inkConfig.id}
                      onClick={() => handleAddProductInkConfig(inkConfig.id)}
                      disabled={addingInkConfig}
                      className="w-full text-left p-4 border border-[#e0e0e0] rounded-[8px] hover:border-[#4285F4] hover:bg-[#f8faff] transition-colors"
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-[14px] font-medium text-[#222]">
                            {inkConfig.name}
                          </div>
                          <div className="text-[12px] text-[#888] mt-1">
                            {inkConfig.inks.map((ink) => ink.name).join(", ")}
                          </div>
                        </div>
                        {basePrice !== null && (
                          <div className="text-[13px] font-medium text-[#4285F4]">
                            {basePrice.toLocaleString()}원
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-[12px] text-[#999] mb-4">
              * 이미 추가된 항목은 표시되지 않습니다
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowAddInkConfigModal(false)}
                className="px-4 py-2.5 text-[13px] text-[#666] hover:text-[#333] transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ===== 옵션 선택 모달 ===== */}
      {showOptionSelectModal && (
        <OptionSelectModal
          productType={productType}
          existingOptions={options}
          onAddPredefined={handleAddPredefinedOption}
          onOpenCustom={() => {
            setShowOptionSelectModal(false);
            setCustomOptionName("");
            setCustomOptionDescription("");
            setShowAddOptionModal(true);
          }}
          onClose={() => setShowOptionSelectModal(false)}
        />
      )}

      {/* ===== 옵션 직접 만들기 모달 ===== */}
      {showAddOptionModal && (
        <Modal setIsModalVisible={setShowAddOptionModal} title="옵션 직접 만들기">
          <div className="w-[70vw] max-w-[400px]">
            <p className="text-[13px] text-[#666] mb-5">
              옵션의 이름을 입력하세요. 생성 후 항목과 값을 추가할 수 있습니다.
            </p>
            <div className="mb-4">
              <label className="block text-[13px] font-medium text-[#555] mb-2">옵션명 *</label>
              <input
                type="text"
                className={modalInput}
                value={customOptionName}
                onChange={(e) => setCustomOptionName(e.target.value)}
                placeholder="예: 코팅, 후가공, 포장"
                autoFocus
              />
            </div>
            <div className="mb-5">
              <label className="block text-[13px] font-medium text-[#555] mb-2">설명 (선택)</label>
              <input
                type="text"
                className={modalInput}
                value={customOptionDescription}
                onChange={(e) => setCustomOptionDescription(e.target.value)}
                placeholder="옵션에 대한 설명"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddOptionModal(false)}
                className="px-4 py-2.5 text-[13px] text-[#666] hover:text-[#333] transition-colors"
              >
                취소
              </button>
              <button
                onClick={async () => {
                  if (!customOptionName.trim()) return;
                  try {
                    const optionRequest: CreateOptionRequest = {
                      name: customOptionName.trim(),
                      description: customOptionDescription || undefined,
                      isRequired: false,
                      isMultipleAllowed: false,
                      quantityType: DEFAULT_NEW_OPTION_QUANTITY_TYPE,
                    };
                    await companyProductSystemUseCase.createOption(
                      companyId!,
                      Number(productId),
                      optionRequest
                    );
                    await loadProduct();
                    setShowAddOptionModal(false);
                    setCustomOptionName("");
                    setCustomOptionDescription("");
                    addNotificationItem(`${customOptionName} 옵션이 추가되었습니다.`);
                  } catch (error) {
                    console.error("옵션 생성 실패:", error);
                    addNotificationItem("옵션을 생성하는 중 오류가 발생했습니다.");
                  }
                }}
                disabled={!customOptionName.trim()}
                className="px-5 py-2.5 bg-[#222] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#444] disabled:bg-[#e0e0e0] disabled:text-[#aaa] transition-colors"
              >
                추가
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ===== 커스텀 사이즈 설정 모달 ===== */}
      {showSizeSettingsModal && (() => {
        const maxPaperWidth = getMaxPaperWidth();
        return (
        <Modal setIsModalVisible={setShowSizeSettingsModal} title="커스텀 사이즈 범위 설정">
          <div className="w-[70vw] max-w-[480px]">
            <p className="text-[13px] text-[#666] mb-3">
              주문자가 선택할 수 있는 사이즈 범위를 설정하세요.
            </p>
            {/* 용지 최대 폭 안내 */}
            {maxPaperWidth ? (
              <div className="mb-4 p-3 bg-[#E8EFFF] border border-[#4285F4]/20 rounded-[8px]">
                <p className="text-[12px] text-[#4285F4]">
                  <span className="font-medium">용지 최대 폭:</span> {maxPaperWidth.toLocaleString()}mm
                  <span className="text-[11px] text-[#4285F4]/70 ml-1">
                    (최대 가로는 이 값까지 설정 가능)
                  </span>
                </p>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-[#FFF8E1] border border-[#FFE082] rounded-[8px]">
                <p className="text-[12px] text-[#F57C00]">
                  용지 관리에서 용지를 추가해야 커스텀 사이즈를 설정할 수 있습니다.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 mb-5">
              {[
                { label: "최소 가로 (mm)", value: minWidth, setter: setMinWidth, key: "minWidth" as const, maxLimit: maxPaperWidth },
                { label: "최대 가로 (mm)", value: maxWidth, setter: setMaxWidth, key: "maxWidth" as const, maxLimit: maxPaperWidth },
                { label: "최소 세로 (mm)", value: minHeight, setter: setMinHeight, key: "minHeight" as const, maxLimit: null },
                { label: "최대 세로 (mm)", value: maxHeight, setter: setMaxHeight, key: "maxHeight" as const, maxLimit: null },
              ].map((item) => {
                const exceedsLimit = item.maxLimit && item.value && item.value > item.maxLimit;
                return (
                <div key={item.label}>
                  <label className="block text-[13px] font-medium text-[#555] mb-2">{item.label}</label>
                  <div className="relative">
                    <input
                      type="number"
                      className={`${modalInput} ${
                        sizeValidationErrors[item.key] || exceedsLimit
                          ? "border-[#FCA5A5] bg-[#FEF2F2] focus:border-[#DC2626]"
                          : ""
                      }`}
                      value={item.value || ""}
                      onChange={(e) => {
                        const newValue = Number(e.target.value) || null;
                        // 최대 가로는 용지 최대 폭으로 제한
                        if (item.maxLimit && newValue && newValue > item.maxLimit) {
                          item.setter(item.maxLimit);
                        } else {
                          item.setter(newValue);
                        }
                        setSizeValidationErrors((prev) => ({ ...prev, [item.key]: undefined }));
                      }}
                      placeholder="0"
                      max={item.maxLimit || undefined}
                    />
                    {sizeValidationErrors[item.key] && (
                      <p className="text-[11px] text-[#DC2626] mt-1">{sizeValidationErrors[item.key]}</p>
                    )}
                    {exceedsLimit && !sizeValidationErrors[item.key] && (
                      <p className="text-[11px] text-[#DC2626] mt-1">
                        용지 최대 폭({item.maxLimit?.toLocaleString()}mm)을 초과할 수 없습니다.
                      </p>
                    )}
                  </div>
                </div>
              )})}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSizeSettingsModal(false)}
                className="px-4 py-2.5 text-[13px] text-[#666] hover:text-[#333] transition-colors"
              >
                취소
              </button>
              <button
                onClick={async () => {
                  const success = await handleSaveSizeSettings();
                  if (success) {
                    setShowSizeSettingsModal(false);
                  }
                }}
                disabled={savingSizeSettings}
                className="px-5 py-2.5 bg-[#222] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#444] disabled:bg-[#e0e0e0] disabled:text-[#aaa] transition-colors"
              >
                {savingSizeSettings ? "저장 중..." : "저장"}
              </button>
            </div>
          </div>
        </Modal>
      );
      })()}

      {/* ===== 커스텀 사이즈 비활성화 확인 모달 ===== */}
      {showDisableCustomSizeModal && (
        <Modal setIsModalVisible={setShowDisableCustomSizeModal} title="커스텀 사이즈 비활성화">
          <div className="w-[70vw] max-w-[400px]">
            <p className="text-[13px] text-[#666] mb-5">
              커스텀 사이즈 주문을 비활성화하시겠습니까?<br />
              비활성화하면 고객이 직접 사이즈를 입력할 수 없습니다.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDisableCustomSizeModal(false)}
                className="px-4 py-2.5 text-[13px] text-[#666] hover:text-[#333] transition-colors"
              >
                취소
              </button>
              <button
                onClick={async () => {
                  setIsSizeCustomizable(false);
                  setShowDisableCustomSizeModal(false);
                  // 서버에 저장
                  if (companyId && productId) {
                    try {
                      await companyProductSystemUseCase.updateProduct(companyId, Number(productId), {
                        isSizeCustomizable: false,
                      });
                      addNotificationItem("커스텀 사이즈가 비활성화되었습니다.");
                    } catch {
                      addNotificationItem("설정 저장에 실패했습니다.");
                    }
                  }
                }}
                className="px-5 py-2.5 bg-[#222] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#444] transition-colors"
              >
                비활성화
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ===== 메인 페이지 (Frame 291048 기반) ===== */}
      <CompanyAdminLayout>
        <div className="p-8 bg-white min-h-screen">

          {/* 2컬럼: 컨텐츠 + 사이드바 */}
          <div className="flex gap-10">

            {/* ===== 왼쪽: 메인 컨텐츠 (플랫 레이아웃, 카드 테두리 없음) ===== */}
            <div className="flex-1 min-w-0">

              {/* 상품 헤더: 썸네일 + 이름 + 설명 */}
              <div className="flex gap-5 mb-12 relative">
                {/* 우상단 설정 토글들 */}
                {isEditing && (
                  <div className="absolute top-0 right-0 flex flex-col gap-3 items-end">
                    {/* 판매 상태 토글 */}
                    <div className="flex items-center gap-2">
                      <span className={`text-[13px] font-medium ${isProductEnabled ? "text-[#4285F4]" : "text-[#888]"}`}>
                        {isProductEnabled ? "판매 중" : "판매 중지"}
                      </span>
                      <button
                        onClick={async () => {
                          const newValue = !isProductEnabled;
                          setIsProductEnabled(newValue);
                          // 즉시 저장
                          if (companyId && productId) {
                            try {
                              await companyProductSystemUseCase.updateProduct(
                                companyId,
                                Number(productId),
                                { isEnable: newValue }
                              );
                              addNotificationItem(newValue ? "상품이 판매 중으로 변경되었습니다." : "상품이 판매 중지 상태로 변경되었습니다.");
                            } catch {
                              setIsProductEnabled(!newValue); // 롤백
                              addNotificationItem("상태 변경에 실패했습니다.");
                            }
                          }
                        }}
                        className={`relative inline-flex h-[26px] w-[48px] items-center rounded-full transition-colors ${
                          isProductEnabled ? "bg-[#4285F4]" : "bg-[#ccc]"
                        }`}
                      >
                        <span
                          className={`inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow transition-transform ${
                            isProductEnabled ? "translate-x-[24px]" : "translate-x-[2px]"
                          }`}
                        />
                      </button>
                    </div>
                    {/* 손지율 적용 토글 */}
                    <div className="flex items-center gap-2">
                      <span className={`text-[13px] font-medium ${applyWasteRate ? "text-[#4285F4]" : "text-[#888]"}`}>
                        손지율 적용
                      </span>
                      <button
                        onClick={async () => {
                          const newValue = !applyWasteRate;
                          setApplyWasteRate(newValue);
                          // 즉시 저장
                          if (companyId && productId) {
                            try {
                              await companyProductSystemUseCase.updateProduct(
                                companyId,
                                Number(productId),
                                { applyWasteRate: newValue }
                              );
                              addNotificationItem(newValue ? "손지율이 적용됩니다." : "손지율이 적용되지 않습니다.");
                            } catch {
                              setApplyWasteRate(!newValue); // 롤백
                              addNotificationItem("설정 변경에 실패했습니다.");
                            }
                          }
                        }}
                        className={`relative inline-flex h-[26px] w-[48px] items-center rounded-full transition-colors ${
                          applyWasteRate ? "bg-[#4285F4]" : "bg-[#ccc]"
                        }`}
                      >
                        <span
                          className={`inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow transition-transform ${
                            applyWasteRate ? "translate-x-[24px]" : "translate-x-[2px]"
                          }`}
                        />
                      </button>
                    </div>
                    {/* 견적 문의 전용 토글 */}
                    <div className="flex items-center gap-2">
                      <span className={`text-[13px] font-medium ${isQuoteOnly ? "text-[#EF6C00]" : "text-[#888]"}`}>
                        견적 문의 전용
                      </span>
                      <button
                        onClick={async () => {
                          const newValue = !isQuoteOnly;
                          setIsQuoteOnly(newValue);
                          // 즉시 저장
                          if (companyId && productId) {
                            try {
                              await companyProductSystemUseCase.updateProduct(
                                companyId,
                                Number(productId),
                                { isQuoteOnly: newValue }
                              );
                              addNotificationItem(
                                newValue
                                  ? "견적 문의 전용으로 설정되었습니다. 쇼핑몰에서 가격 대신 '견적 문의' 버튼이 표시됩니다."
                                  : "일반 상품으로 변경되었습니다."
                              );
                            } catch {
                              setIsQuoteOnly(!newValue); // 롤백
                              addNotificationItem("설정 변경에 실패했습니다.");
                            }
                          }
                        }}
                        className={`relative inline-flex h-[26px] w-[48px] items-center rounded-full transition-colors ${
                          isQuoteOnly ? "bg-[#EF6C00]" : "bg-[#ccc]"
                        }`}
                      >
                        <span
                          className={`inline-block h-[22px] w-[22px] transform rounded-full bg-white shadow transition-transform ${
                            isQuoteOnly ? "translate-x-[24px]" : "translate-x-[2px]"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                )}

                {/* 썸네일 업로드 영역 */}
                <div
                  onClick={handleThumbnailClick}
                  className="w-[180px] h-[180px] bg-[#f5f5f5] rounded-[12px] flex-shrink-0 cursor-pointer overflow-hidden relative group hover:bg-[#eee] transition-colors"
                >
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailChange}
                    className="hidden"
                  />
                  {thumbnailUploading ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[#bbb]">
                      <span className="text-[12px]">업로드 중...</span>
                    </div>
                  ) : thumbnailUrl ? (
                    <>
                      <img
                        src={thumbnailUrl}
                        alt="상품 썸네일"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Camera className="w-8 h-8 text-white" />
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[#bbb]">
                      <Camera className="w-10 h-10 mb-2" />
                      <span className="text-[12px]">이미지 업로드</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 pt-1">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="상품이름"
                    className="w-full text-[22px] font-bold text-[#222] bg-transparent border-none outline-none placeholder-[#ccc] mb-2"
                  />
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="상품 설명을 입력하세요"
                    className="w-full text-[14px] text-[#666] bg-transparent border-none outline-none placeholder-[#bbb] resize-none min-h-[70px] leading-[1.6]"
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={handleSaveProduct}
                      disabled={saving || (isEditing && !hasUnsavedChanges)}
                      className={`px-6 py-2.5 text-[13px] font-medium rounded-[8px] transition-colors ${
                        isEditing && !hasUnsavedChanges
                          ? "bg-[#e8e8e8] text-[#aaa] cursor-default"
                          : "bg-[#222] text-white hover:bg-[#444] disabled:bg-[#e0e0e0] disabled:text-[#aaa]"
                      }`}
                    >
                      {saving ? "저장 중..." : "저장"}
                    </button>
                  </div>
                </div>
              </div>

              {/* ===== 용지 가격 기준 (낱장 상품) ===== */}
              {isEditing && companyId && productId && paperType === "SHEET_PAPER" && !isBookProductType(productType) && (
                <ImpositionSettingsCard
                  companyId={companyId}
                  productId={Number(productId)}
                  initial={impositionSettings}
                  onSaved={setImpositionSettings}
                  notify={addNotificationItem}
                />
              )}

              {/* ===== 상품 상세정보 이미지 ===== */}
              {isEditing && (
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-12">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-left">
                      상품 상세정보 이미지
                    </h2>
                  </div>
                  <div>
                    <input
                      ref={detailPageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!detailPageChangeCallbackRef.current) return;
                        detailPageChangeCallbackRef.current(e);
                      }}
                      className="hidden"
                    />

                    <div className="relative overflow-x-scroll gap-[10px] flex flex-row justify-start items-center h-[180px]">
                      {productDetailImages.map((item, index) => {
                        return (
                          <motion.div
                            key={item.id}
                            layoutId={`detail-image-${item.id}`}
                            layout
                            initial={false}
                            transition={{
                              layout: {
                                duration: 0.3,
                                ease: [0.43, 0.13, 0.23, 0.96],
                              },
                            }}
                            className="flex-shrink-0 flex items-center gap-[10px]"
                          >
                            <div className="flex-shrink-0 relative">
                              <div
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();

                                  if (!companyId || !productId) return;
                                  const confirmed =
                                    window.confirm("정말로 삭제하시겠습니까?");
                                  if (!confirmed) return;
                                  try {
                                    await companyProductUseCase.deleteDetailPage(
                                      companyId,
                                      Number(productId),
                                      item.id,
                                    );
                                    await loadProductDetailImages();
                                  } catch (error) {
                                    console.error("삭제 실패:", error);
                                    alert(
                                      "상세페이지 이미지 삭제에 실패했습니다.",
                                    );
                                  }
                                }}
                                role="button"
                                className="absolute right-[6px] top-[6px] rounded-full w-[25px] h-[25px] bg-[#4285F4] aspect-square justify-center items-center flex"
                              >
                                <LucidePlus
                                  style={{ fill: "currentColor" }}
                                  className="rotate-45 text-white p-1"
                                />
                              </div>
                              {/*Center the div below*/}
                              <div className="h-[30px] left-1/2 -translate-x-1/2 bottom-[15px] bg-white outline outline-gray-300 rounded-full absolute flex w-[80px] justify-between items-center">
                                <div
                                  role="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    moveProductDetalPage("BACKWARD", item.id);
                                  }}
                                >
                                  <ChevronLeft className="text-gray-400" />
                                </div>
                                <div className="text-gray-200">|</div>
                                <div
                                  role="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    moveProductDetalPage("FORWARD", item.id);
                                  }}
                                >
                                  <ChevronRight className="text-gray-400" />
                                </div>
                              </div>
                              <img
                                alt="제품 상세 이미지"
                                src={item.imageUrl ?? ""}
                                className="rounded-[8px] h-[180px] aspect-[2.8/4] object-cover"
                              />
                            </div>
                            {index !== productDetailImages.length - 1 && (
                              <div>
                                <div
                                  role="button"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();

                                    detailPageChangeCallbackRef.current =
                                      async (
                                        e: React.ChangeEvent<HTMLInputElement>,
                                      ) => {
                                        const file = e.target.files?.[0];
                                        if (!file || !companyId || !productId)
                                          return;

                                        try {
                                          await companyProductUseCase.insertDetailPageAt(
                                            companyId,
                                            Number(productId),
                                            file,
                                            item.id,
                                          );
                                          // Refresh only detail page images
                                          await loadProductDetailImages();
                                        } catch (error) {
                                          console.error(
                                            "Failed to add detail page:",
                                            error,
                                          );
                                          alert(
                                            "상세페이지 이미지 추가에 실패했습니다.",
                                          );
                                        }

                                        // Reset the input
                                        e.target.value = "";
                                      };

                                    detailPageInputRef.current?.click();
                                    await loadProductDetailImages();
                                  }}
                                  className="bg-[#4285F4] opacity-[0.5] text-[16px] text-white aspect-square w-[24px] h-[24px] flex justify-center items-center rounded-full"
                                >
                                  <LucidePlus
                                    style={{ fill: "currentColor" }}
                                    className="text-white p-1"
                                  />
                                </div>
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                      <div
                        role="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          detailPageChangeCallbackRef.current = async (
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            const file = e.target.files?.[0];
                            if (!file || !companyId || !productId) return;

                            try {
                              await companyProductUseCase.addDetailPages(
                                companyId,
                                Number(productId),
                                [file],
                              );
                              // Refresh only detail page images
                              await loadProductDetailImages();
                            } catch (error) {
                              console.error("Failed to add detail page:", error);
                              alert("상세페이지 이미지 추가에 실패했습니다.");
                            }

                            // Reset the input
                            e.target.value = "";
                          };

                          detailPageInputRef.current?.click();
                          await loadProductDetailImages();
                        }}
                        className="bg-[#4285F4] opacity-[0.5] text-[16px] text-white aspect-square w-[24px] h-[24px] flex justify-center items-center rounded-full"
                      >
                        <LucidePlus
                          style={{ fill: "currentColor" }}
                          className="text-white p-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ===== 상품 사이즈 관리 ===== */}
              {isEditing && (
                <section id="size-management-section" className="mb-12 overflow-visible">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-[18px] font-bold text-[#222]">상품 사이즈 관리</h2>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setShowAddSizeModal(true)}
                        className="text-[#555] hover:text-[#222] transition-colors"
                      >
                        <Plus className="w-[22px] h-[22px]" />
                      </button>
                      <div className="relative group/custom">
                        <button
                          onClick={() => {
                            if (isSizeCustomizable) {
                              setShowDisableCustomSizeModal(true);
                            } else {
                              // 모달만 열고, 저장할 때 isSizeCustomizable을 true로 변경
                              setShowSizeSettingsModal(true);
                            }
                          }}
                          className={`px-3 py-1.5 text-[13px] font-medium rounded-[6px] transition-colors ${
                            isSizeCustomizable
                              ? "bg-[#E8EFFF] text-[#4285F4] hover:bg-[#D6E4FF]"
                              : "bg-[#F5F5F5] text-[#888] hover:bg-[#EBEBEB]"
                          }`}
                        >
                          {isSizeCustomizable ? "커스텀 허용" : "커스텀 비허용"}
                        </button>
                        {/* 툴팁 */}
                        {isSizeCustomizable && (
                          <div className="absolute top-full right-0 mt-2 px-3 py-2 bg-[#1F2937] text-white text-[12px] rounded-[6px] opacity-0 invisible group-hover/custom:opacity-100 group-hover/custom:visible transition-all z-50 whitespace-nowrap">
                            <div>최소 {minWidth || 0} x {minHeight || 0} mm</div>
                            <div>최대 {maxWidth || 0} x {maxHeight || 0} mm</div>
                            <div className="absolute bottom-full right-4 border-4 border-transparent border-b-[#1F2937]" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {sizeTemplates.length > 0 && (
                    <>
                      <div className="flex flex-wrap gap-4 overflow-visible">
                        {sizeTemplates.map((size) => {
                          const supportingPapers = getSupportingPapers(size.width, size.height);
                          const displayPapers = supportingPapers.slice(0, 2);
                          const remainingCount = supportingPapers.length - 2;

                          // 비율 계산 (최대 120px 기준)
                          const maxDimension = 120;
                          const aspectRatio = size.width / size.height;
                          let displayWidth: number;
                          let displayHeight: number;

                          if (aspectRatio > 1) {
                            displayWidth = maxDimension;
                            displayHeight = maxDimension / aspectRatio;
                          } else {
                            displayHeight = maxDimension;
                            displayWidth = maxDimension * aspectRatio;
                          }

                          return (
                            <div key={size.id} className="w-[160px] group relative">
                              {/* 사이즈 비율 이미지 */}
                              <div className="w-[160px] h-[140px] mb-2 flex items-center justify-center">
                                <div
                                  className="relative"
                                  style={{ width: displayWidth, height: displayHeight }}
                                >
                                  {/* 메인 종이 */}
                                  <div
                                    className={`absolute inset-0 border-2 ${
                                      size.isSupported === false
                                        ? "border-[#FCA5A5] bg-[#FEF2F2]"
                                        : "border-[#999] bg-white"
                                    }`}
                                    style={{
                                      borderRadius: "4px 4px 4px 4px",
                                      clipPath: "polygon(14px 0, 100% 0, 100% 100%, 0 100%, 0 14px)",
                                    }}
                                  />
                                  {/* 접힌 부분 (삼각형 + 대각선) */}
                                  <svg
                                    className="absolute top-0 left-0"
                                    width="15"
                                    height="15"
                                    viewBox="0 0 15 15"
                                  >
                                    {/* 접힌 삼각형 (라운드 꼭짓점) */}
                                    <path
                                      d="M 14 1 L 14 10 Q 14 13 11 13 L 1 13 L 14 1 Z"
                                      fill={size.isSupported === false ? "#FEF2F2" : "white"}
                                      stroke={size.isSupported === false ? "#FCA5A5" : "#999"}
                                      strokeWidth="2"
                                      strokeLinejoin="round"
                                    />
                                    {/* 대각선 (접힌 선) */}
                                    <line
                                      x1="1"
                                      y1="13"
                                      x2="14"
                                      y2="1"
                                      stroke={size.isSupported === false ? "#FCA5A5" : "#999"}
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                </div>
                              </div>

                              {/* 타이틀 (사이즈) */}
                              <div className="flex flex-col mb-1.5">
                                <div className="flex items-center gap-1.5">
                                  <p className={`text-[14px] font-medium ${
                                    size.isSupported === false ? "text-[#DC2626]" : "text-[#333]"
                                  }`}>
                                    {size.name || `${size.width}×${size.height}`}
                                  </p>
                                  {size.isSupported === false && (
                                  <TooltipPortal
                                    content={
                                      <div className="w-[320px] p-4 bg-[#1F2937] text-white text-[12px] rounded-[8px] shadow-lg">
                                        <p className="font-bold text-[13px] mb-2">이 사이즈는 쇼핑몰에 표시되지 않습니다</p>
                                        <p className="text-[#9CA3AF] mb-3">현재 등록된 용지로 인쇄할 수 없어 고객에게 보이지 않습니다.</p>
                                        <p className="font-bold text-[13px] mb-2">해결 방법:</p>
                                        <ul className="text-[#9CA3AF] space-y-1 list-disc pl-4 mb-3">
                                          <li>"용지 관리"에서 더 큰 용지를 추가하세요</li>
                                          <li>또는 "자재 관리"에서 새 용지 규격을 등록하세요</li>
                                        </ul>
                                        <div className="flex gap-2 mt-3">
                                          <button
                                            onClick={() => {
                                              document.getElementById("paper-management-section")?.scrollIntoView({ behavior: "smooth" });
                                              // 스크롤 완료 후 효과 표시
                                              setTimeout(() => {
                                                const btn = document.getElementById("add-paper-button");
                                                if (btn) {
                                                  const rect = btn.getBoundingClientRect();
                                                  // Ripple 효과 요소 생성 (버튼 모양으로)
                                                  const ripple = document.createElement("div");
                                                  ripple.style.cssText = `
                                                    position: fixed;
                                                    left: ${rect.left}px;
                                                    top: ${rect.top}px;
                                                    width: ${rect.width}px;
                                                    height: ${rect.height}px;
                                                    border-radius: 9999px;
                                                    background: rgba(66, 133, 244, 0.5);
                                                    pointer-events: none;
                                                    z-index: 9999;
                                                    animation: ripple-button-expand 600ms ease-out forwards;
                                                  `;
                                                  document.body.appendChild(ripple);
                                                  setTimeout(() => ripple.remove(), 600);
                                                }
                                              }, 400);
                                            }}
                                            className="flex-1 py-2 px-3 bg-white hover:bg-gray-100 text-[#1F2937] text-[12px] font-medium rounded-[6px] text-center transition-colors"
                                          >
                                            용지 추가하기
                                          </button>
                                          <a
                                            href={`/company/${companyId}/material`}
                                            className="flex-1 py-2 px-3 bg-[#4285F4] hover:bg-[#3367D6] text-white text-[12px] font-medium rounded-[6px] text-center transition-colors"
                                          >
                                            자재 관리 →
                                          </a>
                                        </div>
                                      </div>
                                    }
                                  >
                                    <AlertTriangle className="w-[14px] h-[14px] text-[#DC2626] cursor-help" />
                                  </TooltipPortal>
                                  )}
                                </div>
                                {/* 사이즈 이름이 있을 경우 실제 크기 표시 */}
                                {size.name && (
                                  <p className="text-[11px] text-[#888]">{size.width}×{size.height}mm</p>
                                )}
                              </div>

                              {/* chip list (지원 용지) */}
                              <div className="flex flex-wrap gap-1">
                                {displayPapers.map((paperName) => (
                                  <span
                                    key={paperName}
                                    className="px-1.5 py-0.5 text-[11px] font-medium rounded-[4px] bg-[#E8EFFF] text-[#4285F4]"
                                  >
                                    {paperName}
                                  </span>
                                ))}
                                {remainingCount > 0 && (
                                  <TooltipPortal
                                    content={
                                      <div className="p-2 bg-[#1F2937] text-white text-[12px] rounded-[6px] shadow-lg">
                                        <p className="font-medium mb-1">전체 지원 용지</p>
                                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                                          {supportingPapers.map((paperName) => (
                                            <span key={paperName} className="text-[11px] text-[#D1D5DB]">
                                              {paperName}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    }
                                  >
                                    <span className="px-1.5 py-0.5 text-[11px] font-medium rounded-[4px] bg-[#F5F5F5] text-[#888] cursor-help">
                                      +{remainingCount}
                                    </span>
                                  </TooltipPortal>
                                )}
                                {supportingPapers.length === 0 && (
                                  <span className="text-[11px] text-[#bbb]">
                                    지원 용지 없음
                                  </span>
                                )}
                              </div>

                              {/* 호버 시 나타나는 버튼들 */}
                              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {/* 수정 버튼 */}
                                <button
                                  onClick={() => handleEditSize(size)}
                                  className="w-[24px] h-[24px] flex items-center justify-center bg-white/80 rounded-full text-[#bbb] hover:text-[#4285F4] hover:bg-white"
                                >
                                  <Pencil className="w-[12px] h-[12px]" />
                                </button>
                                {/* 삭제 버튼 */}
                                <button
                                  onClick={() => handleDeleteSize(size.id)}
                                  className="w-[24px] h-[24px] flex items-center justify-center bg-white/80 rounded-full text-[#bbb] hover:text-[#F24A4A] hover:bg-white"
                                >
                                  <X className="w-[14px] h-[14px]" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </section>
              )}

              {/* ===== 용지 관리 (비책자 상품만) ===== */}
              {isEditing && !isBookProductType(productType) && (
                <section id="paper-management-section" className="mb-12">
                  <div className="flex justify-between items-center mb-5">
                    <h2 className="text-[18px] font-bold text-[#222]">용지 관리</h2>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowEstimatePreviewModal(true)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#4285F4] text-[#4285F4] text-[13px] font-medium rounded-full hover:bg-[#F5F8FF] transition-colors"
                      >
                        견적식 미리보기
                      </button>
                      <button
                        id="add-paper-button"
                        onClick={() => setShowAddPaperModal(true)}
                        className="add-paper-btn inline-flex items-center gap-1.5 px-4 py-2 bg-[#4285F4] text-white text-[13px] font-medium rounded-full hover:bg-[#3367D6] transition-all"
                      >
                        용지 추가하기
                      </button>
                    </div>
                  </div>

                  {groupedPapers.length === 0 ? (
                    <p className="text-[14px] text-[#bbb] py-8 text-center">연결된 용지가 없습니다</p>
                  ) : (
                    <div className="flex gap-4 flex-wrap">
                      {groupedPapers.map((group) => (
                        <div key={group.materialName} className="w-[200px] group relative p-3 border border-[#e0e0e0] rounded-[12px] bg-white">
                          {/* 썸네일 영역 */}
                          <div className="w-full h-[120px] border border-[#e0e0e0] rounded-[8px] mb-3 bg-[#fafafa]" />
                          {/* 용지 이름 */}
                          <p className="text-[14px] font-medium text-[#333] mb-2">
                            {group.materialName}
                          </p>
                          {/* 평량 chip 리스트 */}
                          <div className="flex flex-wrap gap-1">
                            {group.grammages.length > 0 ? (
                              group.grammages.map((g) => (
                                <span
                                  key={g}
                                  className="px-2 py-0.5 text-[11px] font-medium rounded-[4px] bg-[#4285F4] text-white"
                                >
                                  {g}g
                                </span>
                              ))
                            ) : (
                              <span className="text-[11px] text-[#bbb]">평량 정보 없음</span>
                            )}
                          </div>
                          <ShopUnavailablePaperWarning group={group} />
                          {/* 삭제 버튼 */}
                          <button
                            onClick={() => {
                              if (group.grammages.length >= 2) {
                                // 평량이 2개 이상이면 다이얼로그 표시
                                setDeletePaperGroup(group);
                                setSelectedGrammages(new Set(group.grammages));
                                setShowDeletePaperModal(true);
                              } else {
                                // 평량이 1개면 바로 삭제
                                if (window.confirm("이 용지를 삭제하시겠습니까?")) {
                                  group.paperIds.forEach((id) => handleDeletePaper(id));
                                }
                              }
                            }}
                            className="absolute top-3 right-3 w-[24px] h-[24px] flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-[#bbb] hover:text-[#F24A4A]"
                          >
                            <X className="w-[14px] h-[14px]" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {/* ===== 표지 용지 관리 (책자 상품만) ===== */}
              {isEditing && isBookProductType(productType) && (
                <section id="cover-paper-section" className="mb-12">
                  <div className="flex justify-between items-center mb-5">
                    <h2 className="text-[18px] font-bold text-[#222]">표지 용지 관리</h2>
                    <div className="flex gap-2">
                      <button
                        id="add-cover-paper-button"
                        onClick={() => {
                          setSelectedPaperUsageType("COVER");
                          setShowAddPaperModal(true);
                        }}
                        className="add-paper-btn inline-flex items-center gap-1.5 px-4 py-2 bg-[#4285F4] text-white text-[13px] font-medium rounded-full hover:bg-[#3367D6] transition-all"
                      >
                        표지 용지 추가
                      </button>
                    </div>
                  </div>

                  {(() => {
                    const coverPapers = groupedPapers.filter(g => g.paperUsageType === "COVER");
                    if (coverPapers.length === 0) {
                      return <p className="text-[14px] text-[#bbb] py-8 text-center">표지 용지가 없습니다</p>;
                    }
                    return (
                      <div className="flex gap-4 flex-wrap">
                        {coverPapers.map((group) => (
                          <div key={`cover-${group.materialName}`} className="w-[200px] group relative p-3 border border-[#e0e0e0] rounded-[12px] bg-white">
                            <div className="w-full h-[120px] border border-[#e0e0e0] rounded-[8px] mb-3 bg-[#fafafa]" />
                            <p className="text-[14px] font-medium text-[#333] mb-2">{group.materialName}</p>
                            <div className="flex flex-wrap gap-1">
                              {group.grammages.length > 0 ? (
                                group.grammages.map((g) => (
                                  <span key={g} className="px-2 py-0.5 text-[11px] font-medium rounded-[4px] bg-[#4285F4] text-white">{g}g</span>
                                ))
                              ) : (
                                <span className="text-[11px] text-[#bbb]">평량 정보 없음</span>
                              )}
                            </div>
                            <ShopUnavailablePaperWarning group={group} />
                            <button
                              onClick={() => {
                                if (group.grammages.length >= 2) {
                                  setDeletePaperGroup(group);
                                  setSelectedGrammages(new Set(group.grammages));
                                  setShowDeletePaperModal(true);
                                } else {
                                  if (window.confirm("이 용지를 삭제하시겠습니까?")) {
                                    group.paperIds.forEach((id) => handleDeletePaper(id));
                                  }
                                }
                              }}
                              className="absolute top-3 right-3 w-[24px] h-[24px] flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-[#bbb] hover:text-[#F24A4A]"
                            >
                              <X className="w-[14px] h-[14px]" />
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </section>
              )}

              {/* ===== 내지 용지 관리 (책자 상품만) ===== */}
              {isEditing && isBookProductType(productType) && (
                <section id="inner-paper-section" className="mb-12">
                  <div className="flex justify-between items-center mb-5">
                    <h2 className="text-[18px] font-bold text-[#222]">내지 용지 관리</h2>
                    <div className="flex items-center gap-2">
                      <div className="relative group/inner-custom">
                        <button
                          onClick={handleToggleInnerPaperCustomizable}
                          className={`px-3 py-1.5 text-[13px] font-medium rounded-[6px] transition-colors ${
                            isInnerPaperCustomizable
                              ? "bg-[#E8EFFF] text-[#4285F4] hover:bg-[#D6E4FF]"
                              : "bg-[#F5F5F5] text-[#888] hover:bg-[#EBEBEB]"
                          }`}
                        >
                          {isInnerPaperCustomizable ? "구간 커스텀 허용" : "구간 커스텀 비허용"}
                        </button>
                        {/* 툴팁 */}
                        <div className="absolute top-full right-0 mt-2 px-3 py-2 bg-[#1F2937] text-white text-[12px] rounded-[6px] opacity-0 invisible group-hover/inner-custom:opacity-100 group-hover/inner-custom:visible transition-all z-50 whitespace-nowrap">
                          {isInnerPaperCustomizable
                            ? "고객이 내지를 구간으로 나눠 구간마다 용지를 고를 수 있습니다"
                            : "고객이 내지 용지 하나만 고를 수 있습니다"}
                          <div className="absolute bottom-full right-4 border-4 border-transparent border-b-[#1F2937]" />
                        </div>
                      </div>
                      <button
                        id="add-inner-paper-button"
                        onClick={() => {
                          setSelectedPaperUsageType("INNER");
                          setShowAddPaperModal(true);
                        }}
                        className="add-paper-btn inline-flex items-center gap-1.5 px-4 py-2 bg-[#4285F4] text-white text-[13px] font-medium rounded-full hover:bg-[#3367D6] transition-all"
                      >
                        내지 용지 추가
                      </button>
                    </div>
                  </div>

                  {(() => {
                    const innerPapers = groupedPapers.filter(g => g.paperUsageType === "INNER");
                    if (innerPapers.length === 0) {
                      return <p className="text-[14px] text-[#bbb] py-8 text-center">내지 용지가 없습니다</p>;
                    }
                    return (
                      <div className="flex gap-4 flex-wrap">
                        {innerPapers.map((group) => (
                          <div key={`inner-${group.materialName}`} className="w-[200px] group relative p-3 border border-[#e0e0e0] rounded-[12px] bg-white">
                            <div className="w-full h-[120px] border border-[#e0e0e0] rounded-[8px] mb-3 bg-[#fafafa]" />
                            <p className="text-[14px] font-medium text-[#333] mb-2">{group.materialName}</p>
                            <div className="flex flex-wrap gap-1">
                              {group.grammages.length > 0 ? (
                                group.grammages.map((g) => (
                                  <span key={g} className="px-2 py-0.5 text-[11px] font-medium rounded-[4px] bg-[#4285F4] text-white">{g}g</span>
                                ))
                              ) : (
                                <span className="text-[11px] text-[#bbb]">평량 정보 없음</span>
                              )}
                            </div>
                            <ShopUnavailablePaperWarning group={group} />
                            <button
                              onClick={() => {
                                if (group.grammages.length >= 2) {
                                  setDeletePaperGroup(group);
                                  setSelectedGrammages(new Set(group.grammages));
                                  setShowDeletePaperModal(true);
                                } else {
                                  if (window.confirm("이 용지를 삭제하시겠습니까?")) {
                                    group.paperIds.forEach((id) => handleDeletePaper(id));
                                  }
                                }
                              }}
                              className="absolute top-3 right-3 w-[24px] h-[24px] flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-[#bbb] hover:text-[#F24A4A]"
                            >
                              <X className="w-[14px] h-[14px]" />
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </section>
              )}

              {/* ===== 면지 용지 관리 (양장 상품만) ===== */}
              {isEditing && productType === "HARD_COVER_BOOK" && (
                <section id="end-paper-section" className="mb-12">
                  <div className="flex justify-between items-center mb-5">
                    <h2 className="text-[18px] font-bold text-[#222]">면지 용지 관리</h2>
                    <div className="flex gap-2">
                      <button
                        id="add-end-paper-button"
                        onClick={() => {
                          setSelectedPaperUsageType("END_PAPER");
                          setShowAddPaperModal(true);
                        }}
                        className="add-paper-btn inline-flex items-center gap-1.5 px-4 py-2 bg-[#4285F4] text-white text-[13px] font-medium rounded-full hover:bg-[#3367D6] transition-all"
                      >
                        면지 용지 추가
                      </button>
                    </div>
                  </div>

                  {/* 옛 면지 옵션(텍스트 값 + 수동 추가금)이 남아 있으면 상태에 맞게 안내한다.
                      쇼핑몰에 노출 가능한 면지 용지가 1개 이상일 때만 옛 옵션의 용지 종류·두께가 숨겨지고 그 추가금이 빠진다
                      (백엔드 LegacyEndPaperOptionPolicy — 판정은 카드 경고와 같은 ShopPaperAvailabilityResolver). */}
                  {options.some((opt) => !opt.isDeleted && opt.optionType === "BOOK_END_PAPER") && (() => {
                    const endPaperGroups = groupedPapers.filter((g) => g.paperUsageType === "END_PAPER");
                    const registeredCount = endPaperGroups.reduce((sum, g) => sum + g.paperIds.length, 0);
                    const visibleCount = endPaperGroups.reduce(
                      (sum, g) => sum + g.paperIds.length - (g.shopUnavailablePapers ?? []).length,
                      0,
                    );
                    const notice =
                      visibleCount > 0
                        ? {
                            title: "면지 용지가 기존 면지 옵션을 대체하고 있습니다",
                            body: "쇼핑몰에서는 기존 \"면지\" 옵션의 용지 종류·두께가 숨겨지고 그 추가금도 받지 않습니다. 면지 구성(앞/뒤 장수)은 그대로 쓰입니다.",
                          }
                        : registeredCount > 0
                          ? {
                              title: "면지 용지를 등록했지만 규격·단가가 없어 기존 면지 옵션이 계속 쓰입니다",
                              body: "아래 면지 용지 카드의 경고를 확인해 낱장 규격·단가를 설정하면, 그때부터 쇼핑몰에서 면지 용지 선택으로 바뀝니다.",
                            }
                          : {
                              title: "면지 옵션을 면지 용지로 옮겨 주세요",
                              body: "기존 \"면지\" 옵션의 용지 종류·두께는 더 이상 쓰지 않습니다. 여기서 쇼핑몰에 노출 가능한 면지 용지를 추가하면 기존 옵션의 용지 종류·두께가 숨겨지고 그 추가금도 받지 않습니다.",
                            };
                    return (
                      <div className="mb-4 p-3 rounded-[8px] bg-[#EBF3FF] border border-[#4285F4]/20 text-[12px] leading-[1.5] text-[#4285F4]">
                        <p className="flex items-center gap-1 font-semibold mb-1">
                          <AlertTriangle className="w-[13px] h-[13px] shrink-0" />
                          {notice.title}
                        </p>
                        <p>{notice.body}</p>
                      </div>
                    );
                  })()}

                  {(() => {
                    const endPapers = groupedPapers.filter(g => g.paperUsageType === "END_PAPER");
                    if (endPapers.length === 0) {
                      return <p className="text-[14px] text-[#bbb] py-8 text-center">면지 용지가 없습니다</p>;
                    }
                    return (
                      <div className="flex gap-4 flex-wrap">
                        {endPapers.map((group) => (
                          <div key={`end-${group.materialName}`} className="w-[200px] group relative p-3 border border-[#e0e0e0] rounded-[12px] bg-white">
                            <div className="w-full h-[120px] border border-[#e0e0e0] rounded-[8px] mb-3 bg-[#fafafa]" />
                            <p className="text-[14px] font-medium text-[#333] mb-2">{group.materialName}</p>
                            <div className="flex flex-wrap gap-1">
                              {group.grammages.length > 0 ? (
                                group.grammages.map((g) => (
                                  <span key={g} className="px-2 py-0.5 text-[11px] font-medium rounded-[4px] bg-[#4285F4] text-white">{g}g</span>
                                ))
                              ) : (
                                <span className="text-[11px] text-[#bbb]">평량 정보 없음</span>
                              )}
                            </div>
                            <ShopUnavailablePaperWarning group={group} />
                            <button
                              onClick={() => {
                                if (group.grammages.length >= 2) {
                                  setDeletePaperGroup(group);
                                  setSelectedGrammages(new Set(group.grammages));
                                  setShowDeletePaperModal(true);
                                } else {
                                  if (window.confirm("이 용지를 삭제하시겠습니까?")) {
                                    group.paperIds.forEach((id) => handleDeletePaper(id));
                                  }
                                }
                              }}
                              className="absolute top-3 right-3 w-[24px] h-[24px] flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-[#bbb] hover:text-[#F24A4A]"
                            >
                              <X className="w-[14px] h-[14px]" />
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </section>
              )}

              {/* ===== 인쇄 구성 관리 ===== */}
              {isEditing && (
                <section className="mb-12">
                  <div className="flex justify-between items-center mb-5">
                    <h2 className="text-[18px] font-bold text-[#222]">인쇄 구성 관리</h2>
                    <div className="flex items-center gap-2">
                      {/* 인쇄 방식: 켠 것만 쇼핑몰에 보인다 */}
                      <span className="text-[12px] font-medium text-[#555]">인쇄 방식</span>
                      {([
                        { side: "SINGLE" as const, label: "단면", enabled: isSingleSideEnabled },
                        { side: "DOUBLE" as const, label: "양면", enabled: isDoubleSideEnabled },
                      ]).map(({ side, label, enabled }) => (
                        <button
                          key={side}
                          onClick={() => handleTogglePrintSide(side)}
                          aria-pressed={enabled}
                          title={enabled ? `${label}: 쇼핑몰에 보입니다` : `${label}: 쇼핑몰에서 숨겨집니다`}
                          className={`px-3 py-1.5 text-[13px] font-medium rounded-[6px] transition-colors ${
                            enabled
                              ? "bg-[#E8EFFF] text-[#4285F4] hover:bg-[#D6E4FF]"
                              : "bg-[#F5F5F5] text-[#888] hover:bg-[#EBEBEB]"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    <button
                      onClick={() => setShowAddInkConfigModal(true)}
                      disabled={availableInkConfigsForModal.length === 0}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#4285F4] text-white text-[13px] font-medium rounded-full hover:bg-[#3367D6] disabled:bg-[#e0e0e0] disabled:text-[#aaa] transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      추가
                    </button>
                    </div>
                  </div>

                  {productInkConfigs.length === 0 ? (
                    <p className="text-[14px] text-[#bbb] py-8 text-center">
                      등록된 인쇄 구성이 없습니다. 연결하지 않으면 쇼핑몰에는 회사의 모든 인쇄 구성이 보입니다
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-3">
                      {productInkConfigs.map((config) => (
                        <div
                          key={config.id}
                          className="relative group bg-white border border-[#e0e0e0] rounded-[12px] p-4 min-w-[140px]"
                        >
                          <div className="text-[14px] font-medium text-[#222] mb-1">
                            {config.inkConfigName}
                          </div>
                          <div className="text-[13px] text-[#666]">
                            {(() => {
                              const inkConfig = inkConfigs.find((ic) => ic.id === config.inkConfigId);
                              if (inkConfig && inkConfig.priceTiers.length > 0) {
                                const minPrice = Math.min(...inkConfig.priceTiers.map((t) => t.price));
                                return `${minPrice.toLocaleString()}원~`;
                              }
                              return "-";
                            })()}
                          </div>
                          <button
                            onClick={() => handleDeleteProductInkConfig(config.id)}
                            className="absolute top-2 right-2 w-[24px] h-[24px] flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-[#bbb] hover:text-[#F24A4A]"
                          >
                            <X className="w-[14px] h-[14px]" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {/* ===== 옵션 관리 ===== */}
              {isEditing && (
                <section className="mb-12">
                  <div className="flex justify-between items-center mb-5">
                    <h2 className="text-[18px] font-bold text-[#222]">옵션 관리</h2>
                    <button
                      onClick={() => setShowOptionSelectModal(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#4285F4] text-white text-[13px] font-medium rounded-full hover:bg-[#3367D6] transition-colors"
                    >
                      옵션 추가하기
                    </button>
                  </div>

                  <ProductOptionList
                    companyId={companyId!}
                    productId={Number(productId)}
                    options={options}
                    isBookProduct={isBookProductType(productType)}
                    materials={materials}
                    onDeleteOption={handleDeleteOption}
                    onRefresh={refreshOptions}
                  />

                </section>
              )}

              {/* 기본 정보 (새 상품 모드) */}
              {!isEditing && (
                <section className="max-w-[480px]">
                  <h2 className="text-[18px] font-bold text-[#222] mb-5">기본 정보</h2>
                  <div className="mb-4">
                    <label className="block text-[13px] font-medium text-[#555] mb-2">카테고리</label>
                    <select
                      className={modalInput}
                      value={categoryId || ""}
                      onChange={(e) => setCategoryId(Number(e.target.value) || null)}
                    >
                      <option value="">카테고리를 선택하세요</option>
                      {categories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
                    </select>
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveProduct}
                      disabled={saving}
                      className="px-6 py-2.5 bg-[#222] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#444] disabled:bg-[#e0e0e0] disabled:text-[#aaa] transition-colors"
                    >
                      상품 생성
                    </button>
                  </div>
                </section>
              )}
            </div>

            {/* ===== 오른쪽: 견적식 미리보기 사이드바 ===== */}
            {isEditing && (
              <div className="w-[320px] flex-shrink-0 hidden lg:block">
                <div className="sticky top-8">
                  <div className="bg-white border border-[#e0e0e0] rounded-[12px] overflow-hidden">
                    {/* 상품 이미지 */}
                    {thumbnailUrl && (
                      <div className="rounded-t-[12px] overflow-hidden">
                        <img src={thumbnailUrl} alt={name} className="w-full h-[160px] object-cover" />
                      </div>
                    )}

                    {/* 상품 정보 + 옵션 영역 */}
                    <div className="px-4 pt-4 pb-3 text-left">
                      {/* 상품명 */}
                      <h4 className="text-[16px] font-bold text-[#222] mb-1 text-center">{name || "상품명"}</h4>
                      {/* 설명 */}
                      <p className="text-[13px] text-[#888] leading-relaxed mb-4 text-center">{description || "상품 설명이 없습니다."}</p>

                      {/* 상품 수량 */}
                      <div className="mb-4">
                        <label className="block text-[12px] text-[#888] mb-1.5 text-left">수량</label>
                        <div className="flex items-center border border-[#e0e0e0] rounded-[8px] overflow-hidden">
                          <button
                            onClick={() => setCalcQuantity(Math.max(1, calcQuantity - 1))}
                            className="w-9 h-9 flex items-center justify-center text-[#888] hover:bg-[#f5f5f5] transition-colors"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            value={calcQuantity}
                            onChange={(e) => setCalcQuantity(Math.max(1, Number(e.target.value)))}
                            className="flex-1 h-9 text-center text-[14px] text-[#222] border-x border-[#e0e0e0] focus:outline-none"
                          />
                          <button
                            onClick={() => setCalcQuantity(calcQuantity + 1)}
                            className="w-9 h-9 flex items-center justify-center text-[#888] hover:bg-[#f5f5f5] transition-colors"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* 옵션 영역 */}
                    <div className={`px-4 pb-4 space-y-5 max-h-[400px] overflow-y-auto text-left ${optionsLoading ? "opacity-60 pointer-events-none" : ""}`}>
                      {/* ===== 1. 크기 선택 (가장 먼저) ===== */}
                      {/* 크기 선택 (IMAGE_SELECT_BOX 형식 + 동적 용지 모양) */}
                      {sizeTemplates.length > 0 && (
                        <div>
                          <div className="text-[12px] text-[#888] mb-2 text-left">크기 *</div>
                          <div className="flex flex-wrap gap-3">
                            {sizeTemplates.map((st) => {
                              const isSelected = calcWidth === st.width && calcHeight === st.height;
                              // 비율 계산 (최대 50px 기준, 60x60 박스 내에 표시)
                              const maxDimension = 50;
                              const aspectRatio = st.width / st.height;
                              let displayWidth: number;
                              let displayHeight: number;
                              if (aspectRatio > 1) {
                                displayWidth = maxDimension;
                                displayHeight = maxDimension / aspectRatio;
                              } else {
                                displayHeight = maxDimension;
                                displayWidth = maxDimension * aspectRatio;
                              }
                              return (
                                <button
                                  key={st.id}
                                  onClick={() => {
                                    setCalcWidth(st.width);
                                    setCalcHeight(st.height);
                                  }}
                                  className={`flex flex-col items-center p-2 rounded-[8px] border-2 transition-colors ${
                                    isSelected
                                      ? "border-[#4285F4] bg-[#E8EFFF]"
                                      : "border-[#e0e0e0] hover:border-[#bbb]"
                                  }`}
                                >
                                  {/* 동적 용지 모양 (이미지가 있는 것으로 간주 → 투명 배경) */}
                                  <div
                                    className="w-[60px] h-[60px] rounded-[4px] flex items-center justify-center mb-1"
                                    style={{ backgroundColor: "transparent" }}
                                  >
                                    <div
                                      className="relative"
                                      style={{ width: displayWidth, height: displayHeight }}
                                    >
                                      {/* 메인 종이 */}
                                      <div
                                        className="absolute inset-0 border-2 border-[#999] bg-white"
                                        style={{
                                          borderRadius: "2px",
                                          clipPath: "polygon(8px 0, 100% 0, 100% 100%, 0 100%, 0 8px)",
                                        }}
                                      />
                                      {/* 접힌 부분 (삼각형 + 대각선) */}
                                      <svg
                                        className="absolute top-0 left-0"
                                        width="9"
                                        height="9"
                                        viewBox="0 0 9 9"
                                      >
                                        <path
                                          d="M 8 1 L 8 6 Q 8 7 7 7 L 1 7 L 8 1 Z"
                                          fill="white"
                                          stroke="#999"
                                          strokeWidth="1.5"
                                          strokeLinejoin="round"
                                        />
                                        <line
                                          x1="1"
                                          y1="7"
                                          x2="8"
                                          y2="1"
                                          stroke="#999"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                        />
                                      </svg>
                                    </div>
                                  </div>
                                  <span className="text-[11px] text-[#333] text-center">
                                    {st.width}×{st.height}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* 사이즈 커스텀 입력 (커스텀 허용일 때만 표시) */}
                      {isSizeCustomizable && (
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <div className="text-[12px] text-[#888] mb-2 text-left">가로(mm)</div>
                            <input
                              type="number"
                              value={calcWidth}
                              onChange={(e) => setCalcWidth(Number(e.target.value))}
                              className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[13px] text-[#333] focus:border-[#4285F4] focus:outline-none"
                            />
                          </div>
                          <div className="flex-1">
                            <div className="text-[12px] text-[#888] mb-2 text-left">세로(mm)</div>
                            <input
                              type="number"
                              value={calcHeight}
                              onChange={(e) => setCalcHeight(Number(e.target.value))}
                              className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[13px] text-[#333] focus:border-[#4285F4] focus:outline-none"
                            />
                          </div>
                        </div>
                      )}

                      {/* 커스텀 비허용 + 사이즈 템플릿 없음 = 안내 메시지 */}
                      {!isSizeCustomizable && sizeTemplates.length === 0 && (
                        <div className="p-3 bg-[#FFF8E1] border border-[#FFE082] rounded-[8px]">
                          <p className="text-[12px] text-[#F57C00]">
                            사이즈 템플릿이 없습니다. 위의 '사이즈 관리'에서 사이즈를 추가해주세요.
                          </p>
                        </div>
                      )}

                      <OrderedProductFields printBeforePaper={isPrintBeforePaper(options, isBookProductType(productType))} paper={<>
{/* ===== 2. 용지 선택 ===== */}
                      {/* 일반 상품: 용지 선택 (동적 필터링) */}
                      {!isBookProductType(productType) && groupedPapers.length > 0 && (
                        <>
                          {/* 용지 종류 (IMAGE_SELECT_BOX 형태) */}
                          <div>
                            <label className="block text-[13px] font-medium text-[#222] mb-3 text-left">
                              {PRODUCT_TYPE_LABELS[productType] || "상품"}용지
                            </label>
                            <div className="text-[12px] text-[#888] mb-2 text-left">종류 *</div>
                            <div className="flex flex-wrap gap-3">
                              {/* 전체 용지 종류 표시, 선택 불가능한 것은 비활성화 */}
                              {Array.from(new Set(groupedPapers.map((gp) => gp.materialName))).map((matName, idx) => {
                                const isSelected = selectedMaterialName === matName;
                                const isAvailable = !availableOptions || availableOptions.availableMaterialNames.includes(matName);
                                // 용지별 고유 색상 (TAG_COLORS 활용)
                                const colorSet = TAG_COLORS[idx % TAG_COLORS.length];

                                // 자재 이미지 찾기: productPapers에서 해당 용지명의 materialId를 찾고, materials에서 이미지 확인
                                // 현재 MaterialDto에 이미지 필드가 없으므로 null (추후 이미지 필드 추가 시 대응)
                                const matchingPaper = productPapers.find((pp) => {
                                  const mat = materials.find((m) => m.id === pp.materialId);
                                  const spec = paperSpecs.find((ps) => ps.id === mat?.paperSpecId);
                                  return spec?.name === matName || mat?.name === matName;
                                });
                                const matchingMaterial = matchingPaper ? materials.find((m) => m.id === matchingPaper.materialId) : null;
                                // TODO: MaterialDto에 imageUrl 필드 추가 시 아래 주석 해제
                                // const materialImageUrl = (matchingMaterial as any)?.imageUrl || null;
                                const materialImageUrl: string | null = null;

                                return (
                                  <button
                                    key={matName}
                                    onClick={() => isAvailable && handleMaterialNameSelect(matName)}
                                    disabled={!isAvailable}
                                    className={`flex flex-col items-center p-2 rounded-[8px] border-2 transition-colors ${
                                      isSelected
                                        ? "border-[#4285F4] bg-[#E8EFFF]"
                                        : isAvailable
                                          ? "border-[#e0e0e0] hover:border-[#bbb]"
                                          : "border-[#e0e0e0] bg-[#f5f5f5] opacity-50 cursor-not-allowed"
                                    }`}
                                  >
                                    {/* 용지 이미지 또는 빈 회색 이미지 */}
                                    <div
                                      className="w-[60px] h-[60px] rounded-[4px] flex items-center justify-center overflow-hidden mb-1"
                                      style={{
                                        backgroundColor: materialImageUrl ? "transparent" : "#e5e5e5",
                                      }}
                                    >
                                      {materialImageUrl && (
                                        <img
                                          src={materialImageUrl}
                                          alt={matName}
                                          className="w-full h-full object-cover"
                                        />
                                      )}
                                      {/* 이미지 없으면 빈 회색 박스, 있으면 흰색 배경 */}
                                    </div>
                                    <span className="text-[11px] text-[#333] text-center">
                                      {matName}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* 평량 (드롭다운 형태) */}
                          <div>
                            <div className="text-[12px] text-[#888] mb-2 text-left">평량 *</div>
                            <select
                              value={selectedGrammage || ""}
                              onChange={(e) => {
                                const grammage = e.target.value ? Number(e.target.value) : null;
                                if (grammage) {
                                  handleGrammageSelect(grammage);
                                }
                              }}
                              className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[13px] text-[#333] bg-white focus:border-[#4285F4] focus:outline-none"
                            >
                              <option value="">평량을 선택하세요</option>
                              {Array.from(new Set(groupedPapers.flatMap((gp) => gp.grammages))).sort((a, b) => a - b).map((grammage) => {
                                const isAvailable = !availableOptions || availableOptions.availableGrammages.includes(grammage);
                                return (
                                  <option
                                    key={grammage}
                                    value={grammage}
                                    disabled={!isAvailable}
                                  >
                                    {grammage}g {!isAvailable ? "(선택 불가)" : ""}
                                  </option>
                                );
                              })}
                            </select>
                          </div>

                          {/* 선택 완료 시 Material ID 표시 (디버그용, 필요시 주석 해제) */}
                          {/* {calcMaterialId && (
                            <div className="text-[11px] text-[#888]">
                              선택된 Material ID: {calcMaterialId}
                            </div>
                          )} */}
                        </>
                      )}

                      {/* ===== 3. 책자 상품용 필드 (표지/내지 용지 + 페이지 수) ===== */}
                      {isBookProductType(productType) && (
                        <>
                          {/* 표지 용지 섹션 */}
                          {(() => {
                            const coverPapers = groupedPapers.filter((g) => g.paperUsageType === "COVER");
                            if (coverPapers.length === 0) return null;

                            // 선택된 용지 종류의 평량 목록
                            const selectedCoverGroup = coverPapers.find((g) => g.materialName === selectedCoverMaterialName);
                            const coverGrammages = selectedCoverGroup?.grammages || [];

                            return (
                              <>
                                <div>
                                  <label className="block text-[13px] font-medium text-[#222] mb-3 text-left">
                                    표지 용지
                                  </label>
                                  <div className="text-[12px] text-[#888] mb-2 text-left">종류 *</div>
                                  <div className="flex flex-wrap gap-2">
                                    {coverPapers.map((group) => {
                                      const isSelected = selectedCoverMaterialName === group.materialName;
                                      return (
                                        <button
                                          key={group.materialName}
                                          onClick={() => {
                                            if (isSelected) {
                                              setSelectedCoverMaterialName(null);
                                              setSelectedCoverGrammage(null);
                                            } else {
                                              setSelectedCoverMaterialName(group.materialName);
                                              setSelectedCoverGrammage(null);
                                            }
                                          }}
                                          className={`px-3 py-2 text-[12px] rounded-[6px] border transition-colors ${
                                            isSelected
                                              ? "border-[#4285F4] bg-[#E8EFFF] text-[#4285F4]"
                                              : "border-[#e0e0e0] text-[#555] hover:border-[#bbb]"
                                          }`}
                                        >
                                          {group.materialName}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* 표지 평량 */}
                                <div>
                                  <div className="text-[12px] text-[#888] mb-2 text-left">평량 *</div>
                                  <select
                                    value={selectedCoverGrammage || ""}
                                    onChange={(e) => {
                                      const grammage = e.target.value ? Number(e.target.value) : null;
                                      setSelectedCoverGrammage(grammage);
                                    }}
                                    disabled={!selectedCoverMaterialName}
                                    className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[13px] text-[#333] bg-white focus:border-[#4285F4] focus:outline-none disabled:bg-[#f5f5f5] disabled:text-[#aaa]"
                                  >
                                    <option value="">{selectedCoverMaterialName ? "평량을 선택하세요" : "용지를 먼저 선택하세요"}</option>
                                    {coverGrammages.sort((a, b) => a - b).map((grammage) => (
                                      <option key={grammage} value={grammage}>
                                        {grammage}g
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </>
                            );
                          })()}

                          {/* 내지 용지 섹션 */}
                          {(() => {
                            const innerPapers = groupedPapers.filter((g) => g.paperUsageType === "INNER");
                            if (innerPapers.length === 0) return null;

                            // 내지 구간 커스텀: 구간마다 용지·평량·페이지 수를 고르고 총 페이지 수는 합계로 정해진다
                            if (isInnerPaperCustomizable) {
                              return (
                                <div>
                                  <div className="flex items-center justify-between mb-3">
                                    <label className="text-[13px] font-medium text-[#222] text-left">
                                      내지 용지 (구간)
                                    </label>
                                    <button
                                      onClick={() =>
                                        setCalcInnerSections((prev) => [
                                          ...prev,
                                          { materialName: null, grammage: null, pageCount: 0 },
                                        ])
                                      }
                                      className="text-[12px] font-medium text-[#4285F4] hover:text-[#3367D6] transition-colors"
                                    >
                                      + 구간 추가
                                    </button>
                                  </div>

                                  <div className="space-y-3">
                                    {calcInnerSections.map((section, index) => {
                                      const sectionGroup = innerPapers.find(
                                        (g) => g.materialName === section.materialName
                                      );
                                      const sectionGrammages = sectionGroup?.grammages || [];
                                      const updateSection = (
                                        patch: Partial<(typeof calcInnerSections)[number]>
                                      ) =>
                                        setCalcInnerSections((prev) =>
                                          prev.map((s, i) => (i === index ? { ...s, ...patch } : s))
                                        );

                                      return (
                                        <div
                                          key={index}
                                          className="border border-[#e0e0e0] rounded-[8px] p-3 space-y-2"
                                        >
                                          <div className="flex items-center justify-between">
                                            <span className="text-[12px] font-medium text-[#555]">
                                              구간 {index + 1}
                                            </span>
                                            {calcInnerSections.length > 1 && (
                                              <button
                                                onClick={() =>
                                                  setCalcInnerSections((prev) =>
                                                    prev.filter((_, i) => i !== index)
                                                  )
                                                }
                                                className="text-[12px] text-[#bbb] hover:text-[#F24A4A] transition-colors"
                                              >
                                                삭제
                                              </button>
                                            )}
                                          </div>

                                          <div className="flex flex-wrap gap-2">
                                            {innerPapers.map((group) => {
                                              const isSelected = section.materialName === group.materialName;
                                              return (
                                                <button
                                                  key={group.materialName}
                                                  onClick={() =>
                                                    updateSection(
                                                      isSelected
                                                        ? { materialName: null, grammage: null }
                                                        : { materialName: group.materialName, grammage: null }
                                                    )
                                                  }
                                                  className={`px-3 py-2 text-[12px] rounded-[6px] border transition-colors ${
                                                    isSelected
                                                      ? "border-[#4285F4] bg-[#E8EFFF] text-[#4285F4]"
                                                      : "border-[#e0e0e0] text-[#555] hover:border-[#bbb]"
                                                  }`}
                                                >
                                                  {group.materialName}
                                                </button>
                                              );
                                            })}
                                          </div>

                                          <div className="flex gap-2">
                                            <select
                                              value={section.grammage || ""}
                                              onChange={(e) =>
                                                updateSection({
                                                  grammage: e.target.value ? Number(e.target.value) : null,
                                                })
                                              }
                                              disabled={!section.materialName}
                                              className="flex-1 border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[13px] text-[#333] bg-white focus:border-[#4285F4] focus:outline-none disabled:bg-[#f5f5f5] disabled:text-[#aaa]"
                                            >
                                              <option value="">
                                                {section.materialName ? "평량을 선택하세요" : "용지를 먼저 선택하세요"}
                                              </option>
                                              {[...sectionGrammages]
                                                .sort((a, b) => a - b)
                                                .map((grammage) => (
                                                  <option key={grammage} value={grammage}>
                                                    {grammage}g
                                                  </option>
                                                ))}
                                            </select>
                                            <input
                                              type="number"
                                              value={section.pageCount}
                                              onChange={(e) =>
                                                updateSection({ pageCount: Number(e.target.value) })
                                              }
                                              min={0}
                                              placeholder="페이지 수"
                                              className="w-[110px] border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[13px] text-[#333] focus:border-[#4285F4] focus:outline-none"
                                            />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  <div className="mt-3 flex items-center justify-between">
                                    <span className="text-[12px] text-[#888]">총 페이지 수</span>
                                    <span className="text-[13px] font-medium text-[#222]">
                                      {innerSectionsTotalPageCount}p
                                    </span>
                                  </div>
                                </div>
                              );
                            }

                            // 선택된 용지 종류의 평량 목록
                            const selectedInnerGroup = innerPapers.find((g) => g.materialName === selectedInnerMaterialName);
                            const innerGrammages = selectedInnerGroup?.grammages || [];

                            return (
                              <>
                                <div>
                                  <label className="block text-[13px] font-medium text-[#222] mb-3 text-left">
                                    내지 용지
                                  </label>
                                  <div className="text-[12px] text-[#888] mb-2 text-left">종류 *</div>
                                  <div className="flex flex-wrap gap-2">
                                    {innerPapers.map((group) => {
                                      const isSelected = selectedInnerMaterialName === group.materialName;
                                      return (
                                        <button
                                          key={group.materialName}
                                          onClick={() => {
                                            if (isSelected) {
                                              setSelectedInnerMaterialName(null);
                                              setSelectedInnerGrammage(null);
                                            } else {
                                              setSelectedInnerMaterialName(group.materialName);
                                              setSelectedInnerGrammage(null);
                                            }
                                          }}
                                          className={`px-3 py-2 text-[12px] rounded-[6px] border transition-colors ${
                                            isSelected
                                              ? "border-[#4285F4] bg-[#E8EFFF] text-[#4285F4]"
                                              : "border-[#e0e0e0] text-[#555] hover:border-[#bbb]"
                                          }`}
                                        >
                                          {group.materialName}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* 내지 평량 */}
                                <div>
                                  <div className="text-[12px] text-[#888] mb-2 text-left">평량 *</div>
                                  <select
                                    value={selectedInnerGrammage || ""}
                                    onChange={(e) => {
                                      const grammage = e.target.value ? Number(e.target.value) : null;
                                      setSelectedInnerGrammage(grammage);
                                    }}
                                    disabled={!selectedInnerMaterialName}
                                    className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[13px] text-[#333] bg-white focus:border-[#4285F4] focus:outline-none disabled:bg-[#f5f5f5] disabled:text-[#aaa]"
                                  >
                                    <option value="">{selectedInnerMaterialName ? "평량을 선택하세요" : "용지를 먼저 선택하세요"}</option>
                                    {innerGrammages.sort((a, b) => a - b).map((grammage) => (
                                      <option key={grammage} value={grammage}>
                                        {grammage}g
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </>
                            );
                          })()}

                          {/* 페이지 수 - 구간 커스텀에서는 구간 합계로 정해지므로 따로 입력하지 않는다 */}
                          {!isInnerPaperCustomizable && (
                            <div>
                              <div className="text-[12px] text-[#888] mb-2 text-left">페이지 수 *</div>
                              <input
                                type="number"
                                value={calcPageCount}
                                onChange={(e) => setCalcPageCount(Number(e.target.value))}
                                min={0}
                                placeholder="예: 100"
                                className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[13px] text-[#333] focus:border-[#4285F4] focus:outline-none"
                              />
                            </div>
                          )}
                          {productType === "HARD_COVER_BOOK" && (
                            <div>
                              <div className="text-[12px] text-[#888] mb-2 text-left">면지 용지</div>
                              <select
                                className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[13px] text-[#333] bg-white focus:border-[#4285F4] focus:outline-none"
                                value={calcEndPaperMaterialId || ""}
                                onChange={(e) => setCalcEndPaperMaterialId(e.target.value ? Number(e.target.value) : null)}
                              >
                                <option value="">선택하세요</option>
                                {productPapers.filter((pp) => pp.paperUsageType === "END_PAPER").map((pp) => {
                                  const mat = materials.find((m) => m.id === pp.materialId);
                                  const spec = paperSpecs.find((ps) => ps.id === mat?.paperSpecId);
                                  return (
                                    <option key={pp.id} value={pp.materialId}>
                                      {mat?.name} {spec ? `${spec.grammage}g` : ""}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          )}
                          {/* 간지 옵션이 존재할 때만 간지 수 필드 표시 */}
                          {options.some((opt) => !opt.isDeleted && opt.optionType === "BOOK_INSERT_PAPER") && (
                            <div>
                              <div className="text-[12px] text-[#888] mb-2 text-left">간지 수</div>
                              <input
                                type="number"
                                value={calcInsertCount}
                                onChange={(e) => setCalcInsertCount(Number(e.target.value))}
                                min={0}
                                placeholder="0"
                                className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[13px] text-[#333] focus:border-[#4285F4] focus:outline-none"
                              />
                            </div>
                          )}
                        </>
                      )}

                      </>} printing={<>
{/* ===== 3. 인쇄 옵션 섹션 ===== */}
                      {(() => {
                        // 상품에 실제로 추가된 인쇄 구성만 표시
                        const displayInkConfigs = inkConfigs.filter((ic) =>
                          productInkConfigs.some((pic) => pic.inkConfigId === ic.id)
                        );

                        if (displayInkConfigs.length === 0) return null;

                        return (
                          <div className="space-y-4">
                            <div className="text-[13px] font-medium text-[#222]">인쇄</div>

                            {/* 인쇄 색상 (SELECT_BOX 형태) */}
                            <div>
                              <div className="text-[12px] text-[#888] mb-2">인쇄 색상 *</div>
                              <div className="flex flex-wrap gap-2">
                                {displayInkConfigs.map((ic) => {
                                  const isSelected = calcInkConfigId === ic.id;
                                  return (
                                    <button
                                      key={ic.id}
                                      onClick={() => setCalcInkConfigId(isSelected ? null : ic.id)}
                                      className={`px-3 py-2 text-[12px] rounded-[6px] border transition-colors ${
                                        isSelected
                                          ? "border-[#222] bg-[#222] text-white"
                                          : "border-[#e0e0e0] text-[#555] hover:border-[#bbb]"
                                      }`}
                                    >
                                      {ic.name}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* 인쇄 방식 (단면/양면 SELECT_BOX 형태) */}
                            <div>
                              <div className="text-[12px] text-[#888] mb-2">인쇄 방식 *</div>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  onClick={() => setCalcPrintMethod("SINGLE")}
                                  className={`px-3 py-2 text-[12px] rounded-[6px] border transition-colors ${
                                    calcPrintMethod === "SINGLE"
                                      ? "border-[#222] bg-[#222] text-white"
                                      : "border-[#e0e0e0] text-[#555] hover:border-[#bbb]"
                                  }`}
                                >
                                  단면
                                </button>
                                <button
                                  onClick={() => setCalcPrintMethod("DOUBLE")}
                                  className={`px-3 py-2 text-[12px] rounded-[6px] border transition-colors ${
                                    calcPrintMethod === "DOUBLE"
                                      ? "border-[#222] bg-[#222] text-white"
                                      : "border-[#e0e0e0] text-[#555] hover:border-[#bbb]"
                                  }`}
                                >
                                  양면
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      </>} />
                      {/* ===== 4. 옵션들 (displayOrder 순) ===== */}
                      {/* 표지 용지, 내지 용지는 위에서 productPapers로 이미 표시하므로 제외 */}
                      {options
                        .filter((opt) => {
                          if (opt.isDeleted) return false;
                          if (opt.attributes.length === 0) return false;
                          // 책자 상품의 표지/내지 용지는 productPapers로 관리하므로 제외
                          const paperOptionTypes = ["BOOK_COVER_PAPER", "BOOK_INNER_PAPER"];
                          if (paperOptionTypes.includes(opt.optionType || "")) return false;
                          // 조건을 만족하지 못해 숨겨지는 옵션은 실제 화면과 같이 그리지 않는다
                          return optionVisibility.options[opt.id] !== "HIDDEN";
                        })
                        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                        .map((opt) => {
                        const optionDisabled = optionVisibility.options[opt.id] === "DISABLED";
                        return (
                        <div
                          key={opt.id}
                          className={`pt-3 border-t border-[#f0f0f0] first:border-t-0 first:pt-0 ${
                            optionDisabled ? "opacity-50 pointer-events-none" : ""
                          }`}
                        >
                          <div className="text-[13px] font-medium text-[#222] mb-3 text-left">{opt.name}</div>
                          {opt.attributes
                            .filter((attr) => !attr.isDeleted && optionVisibility.attributes[attr.id] !== "HIDDEN")
                            .map((attr) => {
                            const attributeDisabled = optionVisibility.attributes[attr.id] === "DISABLED";
                            // 조건에서 벗어난 값은 실제 화면과 같이 목록에서 빼거나 선택할 수 없게 둔다
                            const enabledValues = attr.values.filter(
                              (v) => v.isEnabled && optionVisibility.values[v.id] !== "HIDDEN",
                            );
                            const isValueDisabled = (valueId: number) =>
                              optionVisibility.values[valueId] === "DISABLED";
                            const previewType = attr.previewType || "SELECT_BOX";
                            // 실제 화면과 같이 현재 수량에 해당하는 구간 금액을 보여 준다
                            const tierQuantity = resolveTierQuantity(opt, calcQuantity);
                            const unitPriceOf = (v: typeof attr.values[number]) =>
                              resolveValueUnitPrice(v, tierQuantity);

                            // 선택된 값 ID 가져오기 (속성 ID를 키로 사용)
                            const selectedValueId = calcOptions[String(attr.id)];

                            return (
                              <div
                                key={attr.id}
                                className={`mb-4 ${attributeDisabled ? "opacity-50 pointer-events-none" : ""}`}
                              >
                                <div className="text-[12px] text-[#888] mb-2 text-left">{attr.name} {opt.isRequired ? "*" : ""}</div>

                                {/* SELECT_BOX: 버튼 형태 */}
                                {previewType === "SELECT_BOX" && (
                                  <div className="flex flex-wrap gap-2">
                                    {enabledValues.map((v) => {
                                      const isSelected = selectedValueId === v.id;
                                      const disabled = isValueDisabled(v.id);
                                      return (
                                        <button
                                          key={v.id}
                                          disabled={disabled}
                                          onClick={() => {
                                            if (isSelected) {
                                              setCalcOptions((prev) => {
                                                const newOpts = { ...prev };
                                                delete newOpts[String(attr.id)];
                                                return newOpts;
                                              });
                                            } else {
                                              setCalcOptions((prev) => ({ ...prev, [String(attr.id)]: v.id }));
                                            }
                                          }}
                                          className={`px-3 py-2 text-[12px] rounded-[6px] border transition-colors ${
                                            disabled
                                              ? "border-[#eee] text-[#ccc] cursor-not-allowed"
                                              : isSelected
                                              ? "border-[#222] bg-[#222] text-white"
                                              : "border-[#e0e0e0] text-[#555] hover:border-[#bbb]"
                                          }`}
                                        >
                                          {v.value || "기본"}
                                          {unitPriceOf(v) > 0 && (
                                            <span className="ml-1 text-[10px] opacity-70">+{unitPriceOf(v).toLocaleString()}원</span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* CHECK_BOX: 체크박스 형태 */}
                                {previewType === "CHECK_BOX" && (
                                  <div className="flex flex-col gap-2">
                                    {enabledValues.map((v) => {
                                      const isSelected = selectedValueId === v.id;
                                      return (
                                        <label
                                          key={v.id}
                                          className={`flex items-center gap-2 ${
                                            isValueDisabled(v.id) ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
                                          }`}
                                        >
                                          <input
                                            type="checkbox"
                                            disabled={isValueDisabled(v.id)}
                                            checked={isSelected}
                                            onChange={() => {
                                              if (isSelected) {
                                                setCalcOptions((prev) => {
                                                  const newOpts = { ...prev };
                                                  delete newOpts[String(attr.id)];
                                                  return newOpts;
                                                });
                                              } else {
                                                setCalcOptions((prev) => ({ ...prev, [String(attr.id)]: v.id }));
                                              }
                                            }}
                                            className="w-4 h-4 rounded border-[#d0d0d0] text-[#4285F4] focus:ring-[#4285F4]"
                                          />
                                          <span className="text-[13px] text-[#333]">
                                            {v.value || "기본"}
                                            {unitPriceOf(v) > 0 && (
                                              <span className="ml-1 text-[11px] text-[#4285F4]">+{unitPriceOf(v).toLocaleString()}원</span>
                                            )}
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* DROP_DOWN: 드롭다운 형태 */}
                                {previewType === "DROP_DOWN" && (
                                  <select
                                    value={selectedValueId || ""}
                                    onChange={(e) => {
                                      const valueId = e.target.value ? Number(e.target.value) : null;
                                      if (valueId) {
                                        setCalcOptions((prev) => ({ ...prev, [String(attr.id)]: valueId }));
                                      } else {
                                        setCalcOptions((prev) => {
                                          const newOpts = { ...prev };
                                          delete newOpts[String(attr.id)];
                                          return newOpts;
                                        });
                                      }
                                    }}
                                    className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[13px] text-[#333] focus:border-[#4285F4] focus:outline-none"
                                  >
                                    <option value="">선택하세요</option>
                                    {enabledValues.map((v) => (
                                      <option key={v.id} value={v.id} disabled={isValueDisabled(v.id)}>
                                        {v.value || "기본"}
                                        {unitPriceOf(v) > 0 ? ` (+${unitPriceOf(v).toLocaleString()}원)` : ""}
                                      </option>
                                    ))}
                                  </select>
                                )}

                                {/* IMAGE_SELECT_BOX: 이미지가 있는 선택 박스 */}
                                {previewType === "IMAGE_SELECT_BOX" && (
                                  <div className="flex flex-wrap gap-3">
                                    {enabledValues.map((v) => {
                                      const isSelected = selectedValueId === v.id;
                                      const disabled = isValueDisabled(v.id);
                                      return (
                                        <button
                                          key={v.id}
                                          disabled={disabled}
                                          onClick={() => {
                                            if (isSelected) {
                                              setCalcOptions((prev) => {
                                                const newOpts = { ...prev };
                                                delete newOpts[String(attr.id)];
                                                return newOpts;
                                              });
                                            } else {
                                              setCalcOptions((prev) => ({ ...prev, [String(attr.id)]: v.id }));
                                            }
                                          }}
                                          className={`flex flex-col items-center p-2 rounded-[8px] border-2 transition-colors ${
                                            disabled
                                              ? "border-[#f0f0f0] opacity-40 cursor-not-allowed"
                                              : isSelected
                                              ? "border-[#4285F4] bg-[#E8EFFF]"
                                              : "border-[#e0e0e0] hover:border-[#bbb]"
                                          }`}
                                        >
                                          {v.imageUrl ? (
                                            <img
                                              src={v.imageUrl}
                                              alt={v.value || "옵션 이미지"}
                                              className="w-[60px] h-[60px] object-cover rounded-[4px] mb-1"
                                            />
                                          ) : (
                                            <div className="w-[60px] h-[60px] bg-[#f5f5f5] rounded-[4px] mb-1 flex items-center justify-center">
                                              <span className="text-[10px] text-[#bbb]">이미지 없음</span>
                                            </div>
                                          )}
                                          <span className="text-[11px] text-[#333] text-center">
                                            {v.value || "기본"}
                                          </span>
                                          {unitPriceOf(v) > 0 && (
                                            <span className="text-[10px] text-[#4285F4]">+{unitPriceOf(v).toLocaleString()}원</span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* INPUT_NUMBER: 숫자 입력 */}
                                {previewType === "INPUT_NUMBER" && (
                                  <input
                                    type="number"
                                    placeholder="숫자를 입력하세요"
                                    className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[13px] text-[#333] focus:border-[#4285F4] focus:outline-none"
                                    onChange={(e) => {
                                      // 숫자 입력의 경우 첫 번째 값의 ID를 사용하고 값을 저장
                                      if (enabledValues.length > 0) {
                                        setCalcOptions((prev) => ({ ...prev, [String(attr.id)]: enabledValues[0].id }));
                                      }
                                    }}
                                  />
                                )}

                                {/* INPUT_STRING: 텍스트 입력 */}
                                {previewType === "INPUT_STRING" && (
                                  <input
                                    type="text"
                                    placeholder="텍스트를 입력하세요"
                                    className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[13px] text-[#333] focus:border-[#4285F4] focus:outline-none"
                                    onChange={(e) => {
                                      if (enabledValues.length > 0) {
                                        setCalcOptions((prev) => ({ ...prev, [String(attr.id)]: enabledValues[0].id }));
                                      }
                                    }}
                                  />
                                )}

                                {/* INPUT_DATE_TIME: 날짜/시간 입력 */}
                                {previewType === "INPUT_DATE_TIME" && (
                                  <input
                                    type="datetime-local"
                                    className="w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[13px] text-[#333] focus:border-[#4285F4] focus:outline-none"
                                    onChange={(e) => {
                                      if (enabledValues.length > 0) {
                                        setCalcOptions((prev) => ({ ...prev, [String(attr.id)]: enabledValues[0].id }));
                                      }
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ); })}
                    </div>

                    {/* 가격 영역 */}
                    <div className="px-4 pt-3 pb-4 bg-[#fafafa]">
                      {priceResult ? (
                        <>
                          {/* 세부 내역 */}
                          <div className="space-y-1.5 mb-3 text-[12px]">
                            {/* 책자 상품: 표지/내지 분리 표시 */}
                            {priceResult.bookWasteRateInfo?.applied ? (
                              <>
                                {/* 표지 용지 */}
                                {priceResult.bookWasteRateInfo.cover && (
                                  <>
                                    <div className="flex justify-between items-center">
                                      <span className="text-[#666]">표지 용지</span>
                                      <span className="text-[#333]">
                                        {Math.round(priceResult.bookWasteRateInfo.cover.originalPrice).toLocaleString()}원
                                      </span>
                                    </div>
                                    {priceResult.bookWasteRateInfo.cover.wastePrice > 0 && (
                                      <div className="flex justify-between items-center">
                                        <span className="text-[#666]">
                                          표지 손지 <span className="text-[#999]">({priceResult.bookWasteRateInfo.cover.wasteRatePercent}%)</span>
                                        </span>
                                        <span className="text-[#333]">
                                          {Math.round(priceResult.bookWasteRateInfo.cover.wastePrice).toLocaleString()}원
                                        </span>
                                      </div>
                                    )}
                                  </>
                                )}
                                {/* 내지 용지 */}
                                {priceResult.bookWasteRateInfo.inner && (
                                  <>
                                    <div className="flex justify-between items-center">
                                      <span className="text-[#666]">내지 용지</span>
                                      <span className="text-[#333]">
                                        {Math.round(priceResult.bookWasteRateInfo.inner.originalPrice).toLocaleString()}원
                                      </span>
                                    </div>
                                    {priceResult.bookWasteRateInfo.inner.wastePrice > 0 && (
                                      <div className="flex justify-between items-center">
                                        <span className="text-[#666]">
                                          내지 손지 <span className="text-[#999]">({priceResult.bookWasteRateInfo.inner.wasteRatePercent}%)</span>
                                        </span>
                                        <span className="text-[#333]">
                                          {Math.round(priceResult.bookWasteRateInfo.inner.wastePrice).toLocaleString()}원
                                        </span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </>
                            ) : priceResult.wasteRateInfo?.applied ? (
                              /* 단일 용지 상품: 손지율 적용 */
                              <>
                                <div className="flex justify-between items-center">
                                  <span className="text-[#666]">용지</span>
                                  <span className="text-[#333]">
                                    {Math.round(priceResult.wasteRateInfo.originalPrice).toLocaleString()}원
                                  </span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-[#666]">
                                    손지비용 <span className="text-[#999]">({priceResult.wasteRateInfo.wasteRatePercent}%)</span>
                                  </span>
                                  <span className="text-[#333]">
                                    {Math.round(priceResult.wasteRateInfo.wastePrice).toLocaleString()}원
                                  </span>
                                </div>
                              </>
                            ) : priceResult.coverPaperPrice != null && priceResult.innerPaperPrice != null ? (
                              /* 책자 상품: 손지율 미적용 (표지/내지 분리) */
                              <>
                                <div className="flex justify-between items-center">
                                  <span className="text-[#666]">표지 용지</span>
                                  <span className="text-[#333]">{Math.round(priceResult.coverPaperPrice).toLocaleString()}원</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-[#666]">내지 용지</span>
                                  <span className="text-[#333]">{Math.round(priceResult.innerPaperPrice).toLocaleString()}원</span>
                                </div>
                              </>
                            ) : (
                              /* 일반 상품: 손지율 미적용 */
                              <div className="flex justify-between items-center">
                                <span className="text-[#666]">용지</span>
                                <span className="text-[#333]">{priceResult.paperPrice.toLocaleString()}원</span>
                              </div>
                            )}
                            {priceResult.inkPrice > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-[#666]">인쇄</span>
                                <span className="text-[#333]">{priceResult.inkPrice.toLocaleString()}원</span>
                              </div>
                            )}
                            {priceResult.breakdown.optionDetails.map((detail) => (
                              <div key={`${detail.optionId}-${detail.valueId}`} className="flex justify-between items-center">
                                <span className="text-[#666]">{detail.optionName}</span>
                                <span className="text-[#333]">{detail.price.toLocaleString()}원</span>
                              </div>
                            ))}
                          </div>

                          {/* 소계 + 부가세 */}
                          <div className="space-y-1 mb-3 text-[12px]">
                            <div className="flex justify-between items-center">
                              <span className="text-[#888]">소계</span>
                              <span className="text-[#333]">{priceResult.subtotal.toLocaleString()}원</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-[#888]">부가세</span>
                              <span className="text-[#333]">{priceResult.vat.toLocaleString()}원</span>
                            </div>
                          </div>

                          {/* 손지율 정보 */}
                          {priceResult.bookWasteRateInfo?.applied ? (
                            <div className="mb-3 px-2.5 py-1.5 bg-[#FFF8E8] rounded-[4px] space-y-1">
                              {priceResult.bookWasteRateInfo.cover && priceResult.bookWasteRateInfo.cover.wasteSheetCount > 0 && (
                                <div className="text-[11px] text-[#B8860B]">
                                  표지: {priceResult.bookWasteRateInfo.cover.wasteRatePercent}% 적용 · {priceResult.bookWasteRateInfo.cover.originalSheetCount}장 → {priceResult.bookWasteRateInfo.cover.originalSheetCount + priceResult.bookWasteRateInfo.cover.wasteSheetCount}장 (+{priceResult.bookWasteRateInfo.cover.wasteSheetCount}장)
                                </div>
                              )}
                              {priceResult.bookWasteRateInfo.inner && priceResult.bookWasteRateInfo.inner.wasteSheetCount > 0 && (
                                <div className="text-[11px] text-[#B8860B]">
                                  내지: {priceResult.bookWasteRateInfo.inner.wasteRatePercent}% 적용 · {priceResult.bookWasteRateInfo.inner.originalSheetCount}장 → {priceResult.bookWasteRateInfo.inner.originalSheetCount + priceResult.bookWasteRateInfo.inner.wasteSheetCount}장 (+{priceResult.bookWasteRateInfo.inner.wasteSheetCount}장)
                                </div>
                              )}
                            </div>
                          ) : priceResult.wasteRateInfo?.applied && (
                            <div className="mb-3 px-2.5 py-1.5 bg-[#FFF8E8] rounded-[4px]">
                              <span className="text-[11px] text-[#B8860B]">
                                손지율 {priceResult.wasteRateInfo.wasteRatePercent}% 적용 · {priceResult.wasteRateInfo.originalQuantity}부 → {priceResult.wasteRateInfo.effectiveQuantity}부 (+{priceResult.wasteRateInfo.wasteQuantity}부)
                              </span>
                            </div>
                          )}

                          {/* 세네카 두께 (책자 상품) */}
                          {priceResult.spineThickness != null && (
                            <div className="mb-3 px-2.5 py-1.5 bg-[#E8EFFF] rounded-[4px]">
                              <span className="text-[11px] text-[#4285F4]">세네카: {priceResult.spineThickness}mm</span>
                            </div>
                          )}

                          {/* 총금액 */}
                          <div className="flex justify-between items-center pt-2 border-t border-[#ddd]">
                            <span className="text-[14px] font-bold text-[#222]">총금액</span>
                            <span className="text-[18px] font-bold text-[#4285F4]">{priceResult.total.toLocaleString()}원</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between items-center">
                          <span className="text-[14px] font-bold text-[#222]">총금액</span>
                          <span className="text-[18px] font-bold text-[#bbb]">0원</span>
                        </div>
                      )}

                      {/* 가격 계산 버튼 */}
                      <button
                        onClick={handleCalculatePrice}
                        className="w-full h-[42px] mt-3 bg-[#222] text-white text-[14px] font-medium rounded-[8px] hover:bg-[#444] transition-colors"
                      >
                        가격 계산
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 견적식 미리보기 모달 */}
        {showEstimatePreviewModal && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-[1000]"
              onClick={() => setShowEstimatePreviewModal(false)}
            />
            <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4">
              <div
                className="bg-white rounded-[20px] shadow-2xl max-w-[900px] w-full max-h-[80vh] flex flex-col relative"
                onClick={(e) => e.stopPropagation()}
              >
                {/* 헤더 */}
                <div className="flex justify-between items-center p-6 border-b border-[#e0e0e0]">
                  <h3 className="text-[18px] font-bold text-[#222]">견적식 미리보기</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-[13px] text-[#555]">수량:</label>
                      <input
                        type="number"
                        min={1}
                        value={estimateQuantity}
                        onChange={(e) => setEstimateQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-[80px] h-[36px] px-3 border border-[#e0e0e0] rounded-[8px] text-[13px] text-center focus:outline-none focus:border-[#4285F4]"
                      />
                    </div>
                    <button
                      onClick={() => setShowEstimatePreviewModal(false)}
                      className="w-[32px] h-[32px] flex items-center justify-center rounded-full hover:bg-[#f5f5f5] transition-colors"
                    >
                      <X className="w-[20px] h-[20px] text-[#888]" />
                    </button>
                  </div>
                </div>

                {/* 본문 */}
                <div className="flex-1 overflow-auto p-6">
                  {estimateLoading ? (
                    <div className="py-12 text-center text-[14px] text-[#888]">
                      견적식 미리보기를 불러오는 중...
                    </div>
                  ) : estimateError ? (
                    <div className="py-12 text-center text-[14px] text-[#F24A4A]">
                      {estimateError}
                    </div>
                  ) : estimateData && estimateData.sizeTemplates.length > 0 && estimateData.paperOptions.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-[#fafafa]">
                            <th className="p-3 text-[13px] font-medium text-[#555] text-left border border-[#e0e0e0] sticky left-0 bg-[#fafafa] z-10">
                              사이즈
                            </th>
                            {estimateData.paperOptions.map((paper) => (
                              <th
                                key={paper.materialId}
                                className="p-3 text-[13px] font-medium text-[#555] text-center border border-[#e0e0e0] min-w-[140px]"
                              >
                                {paper.displayName}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {estimateData.sizeTemplates.map((size) => (
                            <tr key={size.sizeTemplateId} className="hover:bg-[#fafafa]">
                              <td className="p-3 text-[13px] font-medium text-[#333] border border-[#e0e0e0] sticky left-0 bg-white z-10">
                                {size.displayName}
                              </td>
                              {estimateData.paperOptions.map((paper) => {
                                const cell = estimateData.cells.find(
                                  (c) => c.sizeTemplateId === size.sizeTemplateId && c.materialId === paper.materialId
                                );
                                if (!cell || !cell.isSupported) {
                                  return (
                                    <td
                                      key={paper.materialId}
                                      className="p-3 text-center border border-[#e0e0e0] bg-[#f9f9f9]"
                                    >
                                      <span className="text-[12px] text-[#bbb]">미지원</span>
                                      {cell?.reason && <p className="mt-1 text-[11px] text-[#999]">{cell.reason}</p>}
                                    </td>
                                  );
                                }
                                return (
                                  <td
                                    key={paper.materialId}
                                    className="p-3 text-center border border-[#e0e0e0]"
                                  >
                                    <p className="text-[11px] text-[#888] mb-1">{cell.sheetSpecName}</p>
                                    {cell.impositionCount != null && (
                                      <p className="text-[11px] text-[#4285F4] mb-1">
                                        조판 {cell.impositionCount}개 · {cell.sheetCount?.toLocaleString()}장
                                      </p>
                                    )}
                                    <p className="text-[15px] font-bold text-[#222]">
                                      {cell.unitPrice?.toLocaleString()}원
                                    </p>
                                    <p className="text-[11px] text-[#888]">
                                      (총 {cell.totalPrice?.toLocaleString()}원)
                                    </p>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-6 bg-[#EBF3FF] border border-[#4285F4]/20 rounded-[12px]">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-[48px] h-[48px] bg-[#4285F4]/10 rounded-full flex items-center justify-center mb-4">
                          <AlertTriangle className="w-[24px] h-[24px] text-[#4285F4]" />
                        </div>
                        <p className="text-[15px] font-medium text-[#222] mb-2">
                          {sizeTemplates.length === 0 && groupedPapers.length === 0
                            ? "사이즈와 용지를 추가해주세요"
                            : sizeTemplates.length === 0
                            ? "사이즈 템플릿이 없습니다"
                            : "용지가 없습니다"}
                        </p>
                        <p className="text-[13px] text-[#555] mb-4">
                          {sizeTemplates.length === 0
                            ? "'사이즈 관리'에서 사이즈를 추가해주세요"
                            : "'용지 관리'에서 용지를 추가해주세요"}
                        </p>
                        <button
                          onClick={() => {
                            setShowEstimatePreviewModal(false);
                            setTimeout(() => {
                              const targetId = sizeTemplates.length === 0 ? "size-management-section" : "paper-management-section";
                              document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth" });
                            }, 100);
                          }}
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#4285F4] text-white text-[13px] font-medium rounded-full hover:bg-[#3367D6] transition-colors"
                        >
                          {sizeTemplates.length === 0 ? "사이즈 추가하러 가기" : "용지 추가하러 가기"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* 푸터 */}
                <div className="flex justify-end p-6 border-t border-[#e0e0e0]">
                  <button
                    onClick={() => setShowEstimatePreviewModal(false)}
                    className="px-6 py-2 bg-[#222] text-white text-[14px] font-medium rounded-[8px] hover:bg-[#444] transition-colors"
                  >
                    닫기
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* 용지 평량 삭제 다이얼로그 */}
        {showDeletePaperModal && deletePaperGroup && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-[1000]"
              onClick={() => setShowDeletePaperModal(false)}
            />
            <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4">
              <div
                className="bg-white rounded-[20px] shadow-2xl max-w-[400px] w-full relative"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-8">
                  {/* Close button */}
                  <button
                    onClick={() => setShowDeletePaperModal(false)}
                    className="absolute top-6 right-6 text-black hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>

                  {/* Title */}
                  <h3 className="text-[20px] font-bold text-center text-[#222] mb-2">
                    삭제할 평량 선택
                  </h3>
                  <p className="text-[14px] text-[#666] text-center mb-6">
                    {deletePaperGroup.materialName}
                  </p>

                  {/* Grammage checkboxes */}
                  <div className="space-y-2 mb-6">
                    {deletePaperGroup.grammages.map((grammage, index) => (
                      <label
                        key={grammage}
                        className="flex items-center gap-3 p-3 border border-[#e0e0e0] rounded-[8px] cursor-pointer hover:bg-[#f5f5f5] transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedGrammages.has(grammage)}
                          onChange={(e) => {
                            const newSet = new Set(selectedGrammages);
                            if (e.target.checked) {
                              newSet.add(grammage);
                            } else {
                              newSet.delete(grammage);
                            }
                            setSelectedGrammages(newSet);
                          }}
                          className="w-5 h-5 rounded border-gray-300 text-[#4285F4] focus:ring-[#4285F4]"
                        />
                        <span className="text-[14px] font-medium text-[#333]">{grammage}g</span>
                      </label>
                    ))}
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeletePaperModal(false)}
                      className="flex-1 py-[14px] border border-[#ddd] bg-white text-[#333] rounded-[8px] font-medium text-[15px] hover:bg-[#f5f5f5] transition-colors"
                    >
                      취소
                    </button>
                    <button
                      onClick={async () => {
                        // 선택한 평량에 해당하는 paperId 삭제
                        const idsToDelete = deletePaperGroup.grammages
                          .map((g, i) => selectedGrammages.has(g) ? deletePaperGroup.paperIds[i] : null)
                          .filter((id): id is number => id !== null);

                        for (const id of idsToDelete) {
                          await handleDeletePaper(id);
                        }
                        setShowDeletePaperModal(false);
                        setDeletePaperGroup(null);
                        setSelectedGrammages(new Set());
                      }}
                      disabled={selectedGrammages.size === 0}
                      className="flex-1 py-[14px] bg-[#F24A4A] hover:bg-[#DC2626] disabled:bg-[#ccc] text-white rounded-[8px] font-medium text-[15px] transition-colors"
                    >
                      삭제 ({selectedGrammages.size}개)
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </CompanyAdminLayout>
    </>
  );
};

export default ProductSystemDetailPage;

